import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { tkbAPI, lopHocAPI, giaoVienAPI, monHocAPI, phanCongAPI, blockedCellAPI, fixedCellAPI, gvConstraintAPI } from '../services/api';
import { toast } from 'react-toastify';
import ConfirmDialog from '../components/ConfirmDialog';
import { FiTrash2, FiX, FiSlash, FiLayers, FiLock, FiUnlock, FiGrid, FiClock, FiChevronDown, FiEdit, FiZap, FiCalendar, FiEye } from 'react-icons/fi';

const THU_LIST = [
  { value: 2, label: 'Thứ 2', short: 'T2' },
  { value: 3, label: 'Thứ 3', short: 'T3' },
  { value: 4, label: 'Thứ 4', short: 'T4' },
  { value: 5, label: 'Thứ 5', short: 'T5' },
  { value: 6, label: 'Thứ 6', short: 'T6' },
  { value: 7, label: 'Thứ 7', short: 'T7' }
];

const BUOI_LIST = [
  { value: 'Sang', label: 'Sáng', short: 'S' },
  { value: 'Chieu', label: 'Chiều', short: 'C' }
];

const TIET_LIST = [1, 2, 3, 4, 5];

const LOG_COLORS = {
  active: '#1a73e8',
  done:   '#188038',
  warn:   '#b06000',
  error:  '#c5221f',
  step:   '#80868b',
};

const TKBPage = () => {
  const [activeTab] = useState('tkb'); // 'tkb' | 'result'
  const [resultView] = useState('lop_sang'); // 'lop_sang' | 'lop_chieu' | 'lop_view' | 'gv'
  const [giaoViens, setGiaoViens] = useState([]);
  const [monHocs, setMonHocs] = useState([]);
  const [phanCongs, setPhanCongs] = useState([]);
  const [selectedGvId, setSelectedGvId] = useState('');
  const [highlightGvId, setHighlightGvId] = useState(null);

  const [tkbs, setTkbs] = useState([]);
  const [lopHocs, setLopHocs] = useState([]);
  const [isAutoScheduling, setIsAutoScheduling] = useState(false);
  const [scheduleLog, setScheduleLog] = useState([]); // [{text, type}] type: 'active'|'done'|'warn'|'error'|'step'
  const scheduleLogRef = useRef([]);
  const addLog = (text, type = 'done') => {
    const entry = { text, type, id: Date.now() + Math.random() };
    scheduleLogRef.current = [...scheduleLogRef.current, entry];
    setScheduleLog([...scheduleLogRef.current]);
  };
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [confirmClearBlocked, setConfirmClearBlocked] = useState(false);
  const [allowEdit] = useState(true); // Luôn bật chế độ sửa
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState('view'); // 'view', 'block', 'fixed', 'spread'
  const [blockedCells, setBlockedCells] = useState({});
  const blockedCellsRef = useRef({});
  const blockLimitToastAtRef = useRef({});
  const importBlockedRef = useRef(null);
  const prevViewModeRef = useRef('normal');
  const [fixedCells, setFixedCells] = useState({});
  const fixedCellsRef = useRef({});  // ref để các async closure luôn đọc giá trị mới nhất
  const [unassignedUnits, setUnassignedUnits] = useState([]); // Các đơn vị TKB không xếp được (tiet = null trong DB)
  
  // Buổi đang được chọn trên bảng (để auto điền cho "Xếp buổi")
  const [activeSession, setActiveSession] = useState({ thu: null, buoi: null });
  
  // State cho drag
  const [isDragging, setIsDragging] = useState(false);
  const [dragAction, setDragAction] = useState(null);
  
  // State cho dropdown "Sẽ học"
  const [blockedMenuOpen, setBlockedMenuOpen] = useState(false);
  const [spreadMenuOpen, setSpreadMenuOpen] = useState(false);
  const [selectedMonId, setSelectedMonId] = useState('');
  const [fixedMenuOpen, setFixedMenuOpen] = useState(false);
  const [fixedAction, setFixedAction] = useState('toggle'); // 'toggle' | 'fix' | 'unfix'
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [zoomMenuOpen, setZoomMenuOpen] = useState(false);
  const [activeToolTab, setActiveToolTab] = useState(null); // 'view'|'vung-xep'|'dien-mon'|'tao-tkb'|'co-dinh'|'chinh-sua'
  const [viewMode, setViewMode] = useState('normal'); // 'normal' | 'dim' | 'showBlocked' | 'showFixed'
  const [swapEnabled, setSwapEnabled] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null); // { lopId, thu, buoi, tiet } — ô đang chọn cho "Rời ô"
  const [swapSource, setSwapSource] = useState(null); // { lopId, thu, buoi, tiet }
  const [swapConflictHighlights, setSwapConflictHighlights] = useState({}); // key -> { color, gvId }
  const [overloadMoveDialog, setOverloadMoveDialog] = useState({
    open: false, gvId: null, gvName: '', soTiet: 0, maxTiet: 0,
    sourceThu: null, sourceBuoi: null,
    units: [], selectedUnit: null, suggestedSlots: [], selectedSlot: null,
  });
  const [copyBlockedDialogOpen, setCopyBlockedDialogOpen] = useState(false);
  const [sourceLopForCopy, setSourceLopForCopy] = useState(null);
  const [selectedTargetLops, setSelectedTargetLops] = useState([]);
  const [compactDialog, setCompactDialog] = useState({
    open: false, candidates: [], selected: new Set(), executing: false
  });

  // Zoom level for TKB grid — persisted in localStorage
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('tkb_zoom');
    return saved ? parseInt(saved, 10) : 50;
  });

  // Cấu hình thời khóa biểu
  const thuList = THU_LIST;
  const buoiList = BUOI_LIST;
  const tietList = TIET_LIST;

  // Tìm các slot phù hợp để chuyển 1 đơn vị TKB sang buổi khác
  const findSuggestedSlotsForUnit = (unit, sourceThu, sourceBuoi) => {
    const { lop_id, gv_id } = unit;
    const soTiet = Math.max(1, Number(unit.so_tiet || 1));
    const hasAnyBlocked = Object.keys(blockedCells).length > 0;
    const slots = [];
    for (const thu of THU_LIST) {
      for (const buoi of BUOI_LIST) {
        if (Number(thu.value) === Number(sourceThu) && buoi.value === sourceBuoi) continue;
        for (let tiet = 1; tiet <= 5; tiet++) {
          // Tất cả tiets của unit mới phải nằm trong phạm vi hợp lệ
          if (tiet + soTiet - 1 > 5) continue;
          // Kiểm tra từng tiet của unit: lớp phải có ô bôi & chưa có TKB khác & GV rảnh
          let ok = true;
          for (let offset = 0; offset < soTiet; offset++) {
            const p = tiet + offset;
            const cellKey = `${lop_id}-${thu.value}-${buoi.value}-${p}`;
            if (hasAnyBlocked && !blockedCells[cellKey]) { ok = false; break; }
            if (isFixed(lop_id, thu.value, buoi.value, p)) { ok = false; break; }
            if (findTKB(lop_id, thu.value, buoi.value, p)) { ok = false; break; }
            const gvBusy = tkbs.some(t =>
              String(t.gv_id) === String(gv_id) && Number(t.thu) === Number(thu.value) &&
              p >= Number(t.tiet) && p <= Number(t.tiet) + Math.max(1, Number(t.so_tiet || 1)) - 1
            );
            if (gvBusy) { ok = false; break; }
          }
          if (!ok) continue;
          slots.push({ thu: thu.value, thuLabel: thu.label, buoi: buoi.value, buoiLabel: buoi.label, tiet });
        }
      }
    }
    return slots;
  };

  const findTKB = (lopId, thu, buoi, tiet) => {
    return tkbs.find(t => {
      if (t.lop_id !== lopId || t.thu !== thu || t.buoi !== buoi) return false;
      const soTiet = Math.max(1, Number(t.so_tiet || 1));
      return tiet >= t.tiet && tiet <= (t.tiet + soTiet - 1);
    });
  };

  const fetchAll = async () => {
    setLoading(true);
    setSwapSource(null);
    setSelectedCell(null);
    try {
      const [tkbRes, lopRes, gvRes, monRes, pcRes, blockedRes, fixedRes] = await Promise.all([
        tkbAPI.getAll(),
        lopHocAPI.getAll(),
        giaoVienAPI.getAll(),
        monHocAPI.getAll(),
        phanCongAPI.getAll(),
        blockedCellAPI.getAll(),
        fixedCellAPI.getAll()
      ]);

      const allTkbs = tkbRes.data?.success ? (tkbRes.data.data || []) : [];
      setTkbs(allTkbs.filter(t => t.tiet != null));
      setUnassignedUnits(
        allTkbs
          .filter(t => t.tiet == null)
          .map(t => ({
            gv_id: t.gv_id, mon_id: t.mon_id, lop_id: t.lop_id,
            ten_gv: t.ten_gv, ten_mon: t.ten_mon, ten_lop: t.ten_lop,
            reason: 'no_slot'
          }))
      );
      setLopHocs(lopRes.data?.success ? (lopRes.data.data || []) : []);
      setGiaoViens(gvRes.data?.success ? (gvRes.data.data || []) : []);
      setMonHocs(monRes.data?.success ? (monRes.data.data || []) : []);
      setPhanCongs(pcRes.data?.success ? (pcRes.data.data || []) : []);

      const nextBlocked = {};
      if (blockedRes.data?.success && Array.isArray(blockedRes.data.data)) {
        blockedRes.data.data.forEach(c => {
          const lopKey = c.lop_id || 'all';
          nextBlocked[`${lopKey}-${c.thu}-${c.buoi}-${c.tiet}`] = true;
        });
      }
      setBlockedCells(nextBlocked);
      blockedCellsRef.current = nextBlocked;

      const nextFixed = {};
      if (fixedRes.data?.success && Array.isArray(fixedRes.data.data)) {
        fixedRes.data.data.forEach(c => {
          const lopKey = c.lop_id || 'all';
          nextFixed[`${lopKey}-${c.thu}-${c.buoi}-${c.tiet}`] = true;
        });
      }
      fixedCellsRef.current = nextFixed;
      setFixedCells(nextFixed);
      setSwapConflictHighlights({});
      return allTkbs.filter(t => t.tiet != null);
    } catch (error) {
      toast.error('Lỗi khi tải dữ liệu TKB!');
      console.error(error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Phát hiện xung đột giáo viên từ danh sách tkb truyền vào (không dùng state)
  const detectConflictsFromList = (tkbList) => {
    const conflictMap = {};
    const slots = new Set(tkbList.map(t => `${t.thu}-${t.buoi}-${t.tiet}`));
    slots.forEach(slotKey => {
      const parts = slotKey.split('-');
      const thu2 = Number(parts[0]), buoi2 = parts[1], tiet2 = Number(parts[2]);
      const c = getConflictColorForSlot(tkbList, { thu: thu2, buoi: buoi2, tiet: tiet2 });
      if (c) conflictMap[slotKey] = c;
    });
    setSwapConflictHighlights(conflictMap);
    setHighlightGvId(null);
    const count = Object.keys(conflictMap).length;
    if (count > 0) {
      toast.warning(`Phát hiện ${count} tiết trùng lịch giáo viên.`);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Sync fixedCellsRef với fixedCells state — đảm bảo isFixed() luôn đọc giá trị mới nhất trong async closures
  useEffect(() => {
    fixedCellsRef.current = fixedCells;
  }, [fixedCells]);

  useEffect(() => {
    if (resultView !== 'gv') return;
    if (selectedGvId) return;
    if (giaoViens.length === 0) return;
    setSelectedGvId(giaoViens[0].gv_id);
  }, [resultView, selectedGvId, giaoViens]);

  // Nếu khóa chỉnh sửa thì về chế độ xem
  useEffect(() => {
    if (!allowEdit && editMode !== 'view') {
      setEditMode('view');
      setBlockedMenuOpen(false);
      setSpreadMenuOpen(false);
      setFixedMenuOpen(false);
      setCreateMenuOpen(false);
      setZoomMenuOpen(false);
    }
    if (!allowEdit) {
      setSwapEnabled(false);
      setSwapSource(null);
      setFixedAction('toggle');
      setViewMenuOpen(false);
      setCreateMenuOpen(false);
      setZoomMenuOpen(false);
      setActiveToolTab(prev => (prev && prev !== 'view') ? null : prev);
    }
  }, [allowEdit, editMode]);

  // Nếu đang ở tab kết quả thì luôn về chế độ xem (read-only)
  useEffect(() => {
    if (activeTab === 'result' && editMode !== 'view') {
      setEditMode('view');
      setBlockedMenuOpen(false);
      setSpreadMenuOpen(false);
      setFixedMenuOpen(false);
    }
    if (activeTab === 'result') {
      setSwapSource(null);
      setViewMenuOpen(false);
      setCreateMenuOpen(false);
      setZoomMenuOpen(false);
    }
  }, [activeTab, editMode]);

  useEffect(() => {
    if (editMode === 'spread' && !selectedMonId) {
      setEditMode('view');
    }
  }, [editMode, selectedMonId]);

  
  // Đóng dropdown "Sẽ học" khi click bên ngoài
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (blockedMenuOpen && !e.target.closest('.blocked-menu-wrapper')) {
        setBlockedMenuOpen(false);
      }
      if (spreadMenuOpen && !e.target.closest('.spread-menu-wrapper')) {
        setSpreadMenuOpen(false);
      }
      if (fixedMenuOpen && !e.target.closest('.fixed-menu-wrapper')) {
        setFixedMenuOpen(false);
      }
      if (viewMenuOpen && !e.target.closest('.view-menu-wrapper')) {
        setViewMenuOpen(false);
      }
      if (createMenuOpen && !e.target.closest('.create-menu-wrapper')) {
        setCreateMenuOpen(false);
      }
      if (zoomMenuOpen && !e.target.closest('.zoom-menu-wrapper')) {
        setZoomMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [blockedMenuOpen, spreadMenuOpen, fixedMenuOpen, viewMenuOpen, createMenuOpen, zoomMenuOpen]);

  // Tự động bật/tắt đổi chỗ khi vào/rời tab "Xây dựng TKB"
  useEffect(() => {
    if (activeToolTab === 'tao-tkb') {
      setSwapEnabled(true);
    } else {
      setSwapEnabled(false);
      setSwapSource(null);
    }
  }, [activeToolTab]);

  // Khi nhả chuột ngoài bảng: dừng drag VÀ thoát editMode
  useEffect(() => {
    const onGlobalMouseUp = (e) => {
      if (!isDragging) return;
      setIsDragging(false);
      setDragAction(null);
      // Chỉ thoát editMode nếu nhả chuột bên ngoài bảng TKB
      const insideTable = e.target.closest('.tkb-table') || e.target.closest('.tkb-table-wrapper');
      if (!insideTable && ['block', 'spread', 'fixed'].includes(editMode)) {
        setEditMode('view');
      }
    };
    document.addEventListener('mouseup', onGlobalMouseUp);
    return () => document.removeEventListener('mouseup', onGlobalMouseUp);
  }, [isDragging, editMode]);

  // Kiểm tra ô có bị block không
  const isBlocked = (lopId, thu, buoi, tiet) => {
    const key = `${lopId}-${thu}-${buoi}-${tiet}`;
    return blockedCells[key] === true;
  };

  const getLopSoTietTuan = (lopId) => {
    const lopPhanCongs = phanCongs.filter(pc => String(pc.lop_id) === String(lopId));
    return lopPhanCongs.reduce((sum, pc) => {
      const mon = monHocs.find(m => String(m.mon_id) === String(pc.mon_id));
      return sum + (Number(mon?.so_tiet_tuan ?? pc.so_tiet_tuan) || 0);
    }, 0);
  };

  const countVungXepCellsForLop = (lopId, cells = blockedCellsRef.current) => {
    const prefix = `${lopId}-`;
    return Object.keys(cells).filter(k => k.startsWith(prefix) && cells[k]).length;
  };

  /** Kiểm tra có được thêm ô vùng xếp mới cho lớp (bỏ bôi luôn được phép). */
  const getVungXepBlockAddCheck = (lopId) => {
    const maxTiet = getLopSoTietTuan(lopId);
    const painted = countVungXepCellsForLop(lopId);
    if (maxTiet <= 0) {
      return { ok: false, reason: 'no_phan_cong', maxTiet: 0, painted };
    }
    if (painted >= maxTiet) {
      return { ok: false, reason: 'limit', maxTiet, painted };
    }
    return { ok: true, maxTiet, painted };
  };

  const notifyVungXepBlockLimit = (lopId, check) => {
    const now = Date.now();
    if (blockLimitToastAtRef.current[lopId] && now - blockLimitToastAtRef.current[lopId] < 2500) return;
    blockLimitToastAtRef.current[lopId] = now;
    const lop = lopHocs.find(l => String(l.lop_id) === String(lopId));
    const tenLop = lop?.ten_lop || 'Lớp';
    if (check.reason === 'no_phan_cong') {
      toast.warning(`${tenLop}: chưa có phân công môn (tổng tiết/tuần = 0), không thể bôi thêm vùng xếp.`);
    } else {
      toast.warning(
        `${tenLop}: đã bôi ${check.painted}/${check.maxTiet} tiết/tuần theo phân công — không thể bôi thêm. Bỏ đánh dấu ô khác nếu muốn đổi vị trí.`
      );
    }
  };

  // Toggle block cell
  const toggleBlockCell = async (lopId, thu, buoi, tiet) => {
    const key = `${lopId}-${thu}-${buoi}-${tiet}`;
    const newBlocked = { ...blockedCellsRef.current };
    
    try {
      if (newBlocked[key]) {
        // Xóa blocked cell từ database
        delete newBlocked[key];
        setBlockedCells(newBlocked);
        blockedCellsRef.current = newBlocked;
        // Tìm và xóa từ DB (cần reload để lấy ID)
        const res = await blockedCellAPI.getAll();
        if (res.data.success) {
          const cell = res.data.data.find(c => 
            (c.lop_id || 'all') === lopId && c.thu === thu && c.buoi === buoi && c.tiet === tiet
          );
          if (cell) await blockedCellAPI.delete(cell.bc_id);
        }
      } else {
        const addCheck = getVungXepBlockAddCheck(lopId);
        if (!addCheck.ok) {
          notifyVungXepBlockLimit(lopId, addCheck);
          return;
        }
        // Thêm blocked cell vào database
        newBlocked[key] = true;
        setBlockedCells(newBlocked);
        blockedCellsRef.current = newBlocked;
        try {
          await blockedCellAPI.create({
            lop_id: lopId === 'all' ? null : lopId,
            thu,
            buoi,
            tiet,
            ghi_chu: null
          });
        } catch (error) {
          if (error.response?.status === 409) {
            // Ô đã được đánh dấu, không cần làm gì thêm
            toast.warning('Ô này đã được đánh dấu sẽ học!');
          } else {
            throw error;
          }
        }
      }
    } catch (error) {
      toast.error('Lỗi khi lưu ô chặn!');
      console.error(error);
    }
  };

  // Set block cell (dùng cho drag)
  const setBlockCell = async (lopId, thu, buoi, tiet, blocked) => {
    const key = `${lopId}-${thu}-${buoi}-${tiet}`;
    try {
      if (blocked) {
        if (blockedCellsRef.current[key]) return;
        const addCheck = getVungXepBlockAddCheck(lopId);
        if (!addCheck.ok) {
          notifyVungXepBlockLimit(lopId, addCheck);
          return;
        }
        // Thêm blocked cell
        setBlockedCells(prev => {
          const next = { ...prev, [key]: true };
          blockedCellsRef.current = next;
          return next;
        });
        try {
          await blockedCellAPI.create({
            lop_id: lopId === 'all' ? null : lopId,
            thu,
            buoi,
            tiet,
            ghi_chu: null
          });
        } catch (error) {
          if (error.response?.status === 409) {
            // Ô đã được đánh dấu, không cần làm gì thêm
          } else {
            throw error;
          }
        }
      } else {
        // Xóa blocked cell
        setBlockedCells(prev => {
          const newBlocked = { ...prev };
          delete newBlocked[key];
          blockedCellsRef.current = newBlocked;
          return newBlocked;
        });
        const res = await blockedCellAPI.getAll();
        if (res.data.success) {
          const cell = res.data.data.find(c => 
            (c.lop_id || 'all') === lopId && c.thu === thu && c.buoi === buoi && c.tiet === tiet
          );
          if (cell) await blockedCellAPI.delete(cell.bc_id);
        }
      }
    } catch (error) {
      console.error('Lỗi khi set block cell:', error);
    }
  };

  // Kiểm tra ô có bị fixed không
  const isFixed = (lopId, thu, buoi, tiet) => {
    const key = `${lopId}-${thu}-${buoi}-${tiet}`;
    return fixedCellsRef.current[key] === true;
  };

  // Set fixed cell (dùng cho drag)
  const setFixedCell = async (lopId, thu, buoi, tiet, fixed) => {
    const key = `${lopId}-${thu}-${buoi}-${tiet}`;
    try {
      if (fixed) {
        // Thêm fixed cell
        setFixedCells(prev => ({ ...prev, [key]: true }));
        await fixedCellAPI.create({
          lop_id: lopId === 'all' ? null : lopId,
          thu,
          buoi,
          tiet,
          noi_dung: 'Cố định',
          so_tiet: 1
        });
      } else {
        // Xóa fixed cell
        setFixedCells(prev => {
          const newFixed = { ...prev };
          delete newFixed[key];
          return newFixed;
        });
        await fixedCellAPI.deleteByCell(lopId, thu, buoi, tiet);
      }
    } catch (error) {
      console.error('Lỗi khi set fixed cell:', error);
    }
  };

  // Xử lý bắt đầu drag
  const handleMouseDown = (lopId, thu, buoi, tiet) => {
    if (!allowEdit) return; // Không cho drag khi khóa
    if (editMode === 'view') return;
    
    const existingTKB = findTKB(lopId, thu, buoi, tiet);
    
    if (editMode === 'block') {
      if (existingTKB) return;
      const currentBlocked = isBlocked(lopId, thu, buoi, tiet);
      setIsDragging(true);
      setDragAction(currentBlocked ? 'unblock' : 'block');
      setBlockCell(lopId, thu, buoi, tiet, !currentBlocked);
    } else if (editMode === 'spread') {
      setIsDragging(true);
      setDragAction('spread');
      handleSpreadSubjectAtCell(lopId, thu, buoi, tiet);
    } else if (editMode === 'fixed') {
      if (!existingTKB) return; // Chỉ fixed ô có TKB
      const currentFixed = isFixed(lopId, thu, buoi, tiet);
      const action = fixedAction === 'toggle' ? (currentFixed ? 'unfix' : 'fix') : fixedAction;
      setIsDragging(true);
      setDragAction(action);
      setFixedCell(lopId, thu, buoi, tiet, action === 'fix');
    }
  };

  // Phát hiện trùng lặp giáo viên trong TKB
  // eslint-disable-next-line no-unused-vars
  const handleDetectConflicts = () => {
    const conflictMap = {};
    const slots = new Set();
    
    // Thu thập tất cả các slot có TKB
    tkbs.forEach(t => {
      slots.add(`${t.thu}-${t.buoi}-${t.tiet}`);
    });
    
    // Kiểm tra xung đột cho mỗi slot
    slots.forEach(slotKey => {
      const [thu, buoi, tiet] = slotKey.split('-').map((v, i) => i === 0 ? Number(v) : i === 2 ? Number(v) : v);
      const conflict = getConflictColorForSlot(tkbs, { thu, buoi, tiet });
      if (conflict) {
        conflictMap[slotKey] = conflict;
      }
    });
    
    setSwapConflictHighlights(conflictMap);
    setHighlightGvId(null);
    
    const conflictCount = Object.keys(conflictMap).length;
    if (conflictCount > 0) {
      toast.warning(`Phát hiện ${conflictCount} tiết có trùng lịch giáo viên. Click vào cột "Tiết" để highlight GV trùng lặp.`);
    } else {
      toast.success('Không phát hiện trùng lịch giáo viên nào.');
    }
  };

  // Xử lý kéo qua ô khác
  const handleMouseEnter = (lopId, thu, buoi, tiet) => {
    if (!allowEdit) return; // Không cho drag khi khóa
    if (!isDragging || !dragAction) return;
    
    const existingTKB = findTKB(lopId, thu, buoi, tiet);
    
    if (editMode === 'block') {
      if (existingTKB) return;
      setBlockCell(lopId, thu, buoi, tiet, dragAction === 'block');
    } else if (editMode === 'spread') {
      if (dragAction !== 'spread') return;
      handleSpreadSubjectAtCell(lopId, thu, buoi, tiet);
    } else if (editMode === 'fixed') {
      if (!existingTKB) return;
      if (dragAction !== 'fix' && dragAction !== 'unfix') return;
      setFixedCell(lopId, thu, buoi, tiet, dragAction === 'fix');
    }
  };

  // Xử lý kết thúc drag (trong bảng) - chỉ dừng drag, giữ nguyên editMode
  const handleMouseUp = () => {
    setIsDragging(false);
    setDragAction(null);
  };

  // Xử lý click vào ô TKB
  const handleCellClick = async (lopId, thu, buoi, tiet) => {
    // Ghi nhận buổi đang thao tác để auto điền cho "Xếp buổi" (kể cả khi đang khóa sửa)
    if (Number.isFinite(Number(thu)) && thu >= 2 && (buoi === 'Sang' || buoi === 'Chieu')) {
      setActiveSession({ thu, buoi });
    }

    // Nếu đang khóa sửa: chỉ cho phép click để highlight GV, không thao tác block/fixed
    if (!allowEdit) return;

    if (editMode === 'spread') {
      await handleSpreadSubjectAtCell(lopId, thu, buoi, tiet);
      return;
    }

    if (editMode === 'block') {
      const existingTKB = findTKB(lopId, thu, buoi, tiet);
      if (existingTKB) return; // Không đánh dấu lên ô đã có tiết
      toggleBlockCell(lopId, thu, buoi, tiet);
      return;
    }

    if (editMode === 'fixed') {
      const existingTKB = findTKB(lopId, thu, buoi, tiet);
      if (!existingTKB) return;
      if (fixedAction === 'toggle') {
        const key = `${lopId}-${thu}-${buoi}-${tiet}`;
        const currentFixed = fixedCells[key];
        setFixedCell(lopId, thu, buoi, tiet, !currentFixed);
      } else {
        setFixedCell(lopId, thu, buoi, tiet, fixedAction === 'fix');
      }
      return;
    }
  };

  const toggleHighlightTeacher = (gvId) => {
    if (!gvId) {
      setHighlightGvId(null);
      return;
    }
    setHighlightGvId((prev) => (String(prev) === String(gvId) ? prev : gvId));
  };

  // Xóa toàn bộ TKB
  const handleDeleteAllTKB = async () => {
    try {
      const result = await tkbAPI.deleteAll();
      toast.success(result.data.message || 'Đã xóa toàn bộ TKB!');
      setConfirmDeleteAll(false);
      // Xóa unassigned units khi xóa toàn bộ TKB
      setUnassignedUnits([]);
      // Tự động loại bỏ đánh dấu cố định
      if (Object.keys(fixedCells).length > 0) {
        try {
          await fixedCellAPI.deleteAll();
          setFixedCells({});
        } catch (_) {}
      }
      fetchAll();
    } catch (error) {
      toast.error('Không thể xóa TKB!');
    }
  };



  // Core: xếp TKB tất cả các buổi, nhận initialTkbs để tránh dùng state cũ
  // overrideFixedCells: nếu truyền vào thì dùng thay cho fixedCells state (tránh stale closure)
  const scheduleAllCore = async (initialTkbs, overrideFixedCells) => {
    const MAX_RETRY = 10;
    let currentTkbs = [...initialTkbs];
    const findAllConflictUnits = (records, thu, buoi) => {
      const gvTietMap = {};
      for (const r of records) {
        if (r.tiet == null) continue;
        const soTiet = Math.max(1, Number(r.so_tiet || 1));
        for (let i = 0; i < soTiet; i++) {
          const k = `${r.gv_id}|${r.tiet + i}`;
          if (!gvTietMap[k]) gvTietMap[k] = [];
          gvTietMap[k].push(r);
        }
      }
      const toMove = new Map();
      for (const units of Object.values(gvTietMap)) {
        if (units.length <= 1) continue;
        const movable = units.filter(u => {
          const soTiet = Math.max(1, Number(u.so_tiet || 1));
          for (let i = 0; i < soTiet; i++) {
            if (isFixed(u.lop_id, thu, buoi, u.tiet + i)) return false;
          }
          return true;
        });
        if (movable.length > 0 && !toMove.has(movable[0].tkb_id)) {
          toMove.set(movable[0].tkb_id, movable[0]);
        }
      }
      return [...toMove.values()];
    };

    const findSlot = (unit, thu, buoi, curTkbs) => {
      const { lop_id, gv_id } = unit;
      const soTiet = Math.max(1, Number(unit.so_tiet || 1));
      const hasAnyBlocked = Object.keys(blockedCells).length > 0;
      for (const t of THU_LIST) {
        for (const b of BUOI_LIST) {
          if (Number(t.value) === Number(thu) && b.value === buoi) continue;
          for (let tiet = 1; tiet + soTiet - 1 <= 5; tiet++) {
            let ok = true;
            for (let offset = 0; offset < soTiet; offset++) {
              const p = tiet + offset;
              const cellKey = `${lop_id}-${t.value}-${b.value}-${p}`;
              if (hasAnyBlocked && !blockedCells[cellKey]) { ok = false; break; }
              if (isFixed(lop_id, t.value, b.value, p)) { ok = false; break; }
              const occupied = curTkbs.some(tk => {
                if (tk.lop_id !== lop_id || Number(tk.thu) !== Number(t.value) || tk.buoi !== b.value) return false;
                const st = Math.max(1, Number(tk.so_tiet || 1));
                return p >= Number(tk.tiet) && p <= Number(tk.tiet) + st - 1;
              });
              if (occupied) { ok = false; break; }
              const gvBusy = curTkbs.some(tk =>
                String(tk.gv_id) === String(gv_id) && Number(tk.thu) === Number(t.value) &&
                tk.buoi === b.value &&
                p >= Number(tk.tiet) && p <= Number(tk.tiet) + Math.max(1, Number(tk.so_tiet || 1)) - 1
              );
              if (gvBusy) { ok = false; break; }
            }
            if (ok) return { thu: t.value, buoi: b.value, tiet };
          }
        }
      }
      return null;
    };

    // Tìm đơn vị ở buổi khác để hoán đổi nội dung GV (reiO style) khi không có ô trống
    const autoReiO = (unit, failingThu, failingBuoi, curTkbs) => {
      const srcGvId = String(unit.gv_id);
      const srcSoTiet = Math.max(1, Number(unit.so_tiet || 1));
      for (const tgt of curTkbs) {
        if (Number(tgt.thu) === Number(failingThu) && tgt.buoi === failingBuoi) continue;
        if (!tgt.gv_id || tgt.tiet == null) continue;
        if (isFixed(tgt.lop_id, tgt.thu, tgt.buoi, tgt.tiet)) continue;
        if (String(tgt.gv_id) === srcGvId) continue; // cùng GV, đổi vô nghĩa
        const tgtGvId = String(tgt.gv_id);
        const tgtSoTiet = Math.max(1, Number(tgt.so_tiet || 1));
        // srcGV phải rảnh ở vị trí đích
        let srcBusy = false;
        for (let i = 0; i < srcSoTiet && !srcBusy; i++) {
          const p = Number(tgt.tiet) + i;
          if (curTkbs.some(t =>
            t.tkb_id !== tgt.tkb_id && String(t.gv_id) === srcGvId &&
            Number(t.thu) === Number(tgt.thu) && t.buoi === tgt.buoi &&
            p >= Number(t.tiet) && p <= Number(t.tiet) + Math.max(1, Number(t.so_tiet || 1)) - 1
          )) srcBusy = true;
        }
        if (srcBusy) continue;
        // tgtGV phải rảnh ở vị trí nguồn (buổi đang bị lỗi)
        let tgtBusy = false;
        for (let i = 0; i < tgtSoTiet && !tgtBusy; i++) {
          const p = Number(unit.tiet) + i;
          if (curTkbs.some(t =>
            t.tkb_id !== unit.tkb_id && String(t.gv_id) === tgtGvId &&
            Number(t.thu) === Number(failingThu) && t.buoi === failingBuoi &&
            p >= Number(t.tiet) && p <= Number(t.tiet) + Math.max(1, Number(t.so_tiet || 1)) - 1
          )) tgtBusy = true;
        }
        if (tgtBusy) continue;
        return tgt;
      }
      return null;
    };

    const scheduleOneSession = async (thu, buoi, curTkbs) => {
      for (let attempt = 0; attempt <= MAX_RETRY; attempt++) {
        let failed = false, failedRecords = [];
        try {
          const result = await tkbAPI.scheduleSessionBacktrack(thu, buoi, blockedCells, overrideFixedCells ?? fixedCells);
          if (result.data.success) return { ok: true, currentTkbs: curTkbs };
        } catch (err) {
          failed = true;
          failedRecords = err.response?.data?.data?.updatedRecords || [];
        }
        if (!failed) break;
        if (attempt === MAX_RETRY) return { ok: false, currentTkbs: curTkbs };

        const unitsToMove = findAllConflictUnits(failedRecords, thu, buoi);
        if (unitsToMove.length === 0) return { ok: false, currentTkbs: curTkbs };

        let anyMoved = false;
        for (const unit of unitsToMove) {
          // Ưu tiên: tìm ô trống ở buổi khác
          const targetSlot = findSlot(unit, thu, buoi, curTkbs);
          if (targetSlot) {
            try {
              await tkbAPI.update(unit.tkb_id, buildUpdatePayload(unit, targetSlot));
              curTkbs = curTkbs.map(t =>
                t.tkb_id === unit.tkb_id
                  ? { ...t, thu: targetSlot.thu, buoi: targetSlot.buoi, tiet: targetSlot.tiet }
                  : t
              );
              anyMoved = true;
            } catch (e) {
              console.warn('scheduleOneSession move failed:', e?.response?.data?.message || e.message);
            }
            continue;
          }
          // Fallback: hoán đổi nội dung GV (reiO style) khi không có ô trống
          const swapTarget = autoReiO(unit, thu, buoi, curTkbs);
          if (swapTarget) {
            try {
              await Promise.all([
                tkbAPI.update(unit.tkb_id, {
                  gvId: swapTarget.gv_id, monId: swapTarget.mon_id, lopId: unit.lop_id,
                  thu: Number(thu), tiet: unit.tiet,
                  soTiet: Math.max(1, Number(swapTarget.so_tiet || 1)),
                  buoi, skipOverlapCheck: true,
                }),
                tkbAPI.update(swapTarget.tkb_id, {
                  gvId: unit.gv_id, monId: unit.mon_id, lopId: swapTarget.lop_id,
                  thu: Number(swapTarget.thu), tiet: swapTarget.tiet,
                  soTiet: Math.max(1, Number(unit.so_tiet || 1)),
                  buoi: swapTarget.buoi, skipOverlapCheck: true,
                }),
              ]);
              curTkbs = curTkbs.map(t => {
                if (t.tkb_id === unit.tkb_id) return { ...t, gv_id: swapTarget.gv_id, mon_id: swapTarget.mon_id };
                if (t.tkb_id === swapTarget.tkb_id) return { ...t, gv_id: unit.gv_id, mon_id: unit.mon_id };
                return t;
              });
              anyMoved = true;
            } catch (e) {
              console.warn('scheduleOneSession swap failed:', e?.response?.data?.message || e.message);
            }
          }
        }
        if (!anyMoved) return { ok: false, currentTkbs: curTkbs };
      }
      return { ok: false, currentTkbs: curTkbs };
    };

    for (const thu of THU_LIST) {
      for (const buoi of BUOI_LIST) {
        const hasUnits = currentTkbs.some(t =>
          Number(t.thu) === Number(thu.value) && t.buoi === buoi.value
        );
        if (!hasUnits) continue;
        addLog(`  Xếp ${thu.label} ${buoi.value === 'Sang' ? 'Sáng' : 'Chiều'}...`, 'active');
        const { ok, currentTkbs: updatedTkbs } = await scheduleOneSession(thu.value, buoi.value, currentTkbs);
        currentTkbs = updatedTkbs;
        if (ok) {
          addLog(`  ✓ ${thu.label} ${buoi.value === 'Sang' ? 'Sáng' : 'Chiều'}`, 'done');
        } else {
          addLog(`  ⚠ ${thu.label} ${buoi.value === 'Sang' ? 'Sáng' : 'Chiều'} — còn xung đột`, 'warn');
        }
      }
    }
  };

  // Đếm số cặp GV bị xung đột (cùng thu-buoi-tiet) trong danh sách TKB
  const countGvConflicts = (tkbList) => {
    const counts = {};
    for (const t of tkbList) {
      if (t.tiet == null || !t.gv_id) continue;
      const k = `${t.gv_id}|${t.thu}|${t.buoi}|${t.tiet}`;
      counts[k] = (counts[k] || 0) + 1;
    }
    return Object.values(counts).filter(c => c > 1).length;
  };

  // Dàn trải đơn vị TKB → Tinh chỉnh → Xếp TKB các buổi
  // Nếu sau khi xếp vẫn còn xung đột → tiếp tục điều chỉnh + xếp lại (tối đa MAX_OUTER_ROUNDS vòng)
  const handleSpreadUnits = async () => {
    // Reset log
    scheduleLogRef.current = [];
    setScheduleLog([]);
    setIsAutoScheduling(true);
    const MAX_ADJ_ROUNDS = 20;
    const MAX_OUTER_ROUNDS = 5;
    try {
      // Bước -1: Kiểm tra yêu cầu giáo viên
      addLog('Kiểm tra yêu cầu thời gian giáo viên...', 'active');
      try {
        const valRes = await gvConstraintAPI.validateAll();
        const { errors = [], warnings = [], ok } = valRes.data?.data || {};
        if (!ok && errors.length > 0) {
          errors.slice(0, 12).forEach(e => addLog(`  ✗ ${e.message}`, 'error'));
          warnings.slice(0, 6).forEach(w => addLog(`  ⚠ ${w.message}`, 'warn'));
          const preview = errors.slice(0, 6).map(e => `• ${e.message}`).join('\n');
          const proceed = window.confirm(
            `Yêu cầu giáo viên phi logic hoặc xung đột (${errors.length} lỗi):\n\n${preview}${errors.length > 6 ? '\n…' : ''}\n\nVẫn tiếp tục xếp TKB?`
          );
          if (!proceed) {
            addLog('✗ Đã hủy xếp TKB do yêu cầu GV.', 'error');
            toast.warning('Đã hủy xếp TKB. Hãy chỉnh yêu cầu giáo viên rồi thử lại.');
            return;
          }
          addLog('  ⚠ Người dùng chọn tiếp tục dù có lỗi yêu cầu GV', 'warn');
        } else if (warnings.length > 0) {
          warnings.slice(0, 10).forEach(w => addLog(`  ⚠ ${w.message}`, 'warn'));
          toast.warning(`Có ${warnings.length} cảnh báo yêu cầu GV — xem nhật ký xếp lịch.`);
          addLog(`  ✓ Không có lỗi cứng — ${warnings.length} cảnh báo`, 'done');
        } else {
          addLog('  ✓ Yêu cầu GV hợp lệ', 'done');
        }
      } catch (valErr) {
        addLog('  ⚠ Không kiểm tra được yêu cầu GV (bỏ qua)', 'warn');
      }

      // Bước 0: Xếp & cố định Sinh hoạt vào Tiết 1, Thứ 2, đầu buổi của lớp
      addLog('0. Xếp & cố định Sinh hoạt vào đầu buổi Thứ 2...', 'active');
      let currentFixedCells = { ...fixedCells };
      try {
        const shRes = await tkbAPI.placeSinhHoat();
        if (shRes.data?.success) {
          const { placed, cells, message } = shRes.data.data || {};
          if (placed > 0) {
            cells.forEach(c => { currentFixedCells[c.key] = true; });
            fixedCellsRef.current = currentFixedCells;
            setFixedCells(currentFixedCells);
            addLog(`  ✓ ${message}`, 'done');
          } else {
            addLog(`  — ${message || 'Không có Sinh hoạt để xếp'}`, 'done');
          }
        }
      } catch (shErr) {
        addLog('  ⚠ Bỏ qua bước 0 (lỗi Sinh hoạt)', 'warn');
      }

      // Bước 1: Dàn trải
      addLog('1. Dàn trải đơn vị TKB...', 'active');
      const result = await tkbAPI.spreadUnits(blockedCells, currentFixedCells);
      if (!result.data.success) {
        addLog(`✗ Lỗi: ${result.data.message || 'Có lỗi khi dàn trải!'}`, 'error');
        return;
      }
      const { inserted, total, failed } = result.data.data || {};
      if (failed > 0) {
        addLog(`  ⚠ Dàn trải ${inserted}/${total} tiết — ${failed} tiết không xếp được (hàng tạm)`, 'warn');
      } else {
        addLog(`  ✓ Dàn trải ${inserted}/${total} tiết thành công`, 'done');
      }

      for (let outer = 0; outer < MAX_OUTER_ROUNDS; outer++) {
        const roundLabel = MAX_OUTER_ROUNDS > 1 ? ` (vòng ${outer + 1}/${MAX_OUTER_ROUNDS})` : '';

        // Bước 2: Tinh chỉnh lịch GV
        addLog(`2. Tinh chỉnh lịch GV${roundLabel}...`, 'active');
        for (let round = 0; round < MAX_ADJ_ROUNDS; round++) {
          const adj = await tkbAPI.adjustTeacherSessions(currentFixedCells);
          if (!adj.data.success) break;
          const msg = adj.data.message || '';
          if (msg.includes('đã tối ưu') || round === MAX_ADJ_ROUNDS - 1) {
            addLog(`  ✓ ${msg}`, 'done');
            break;
          }
        }

        // Bước 2.5: Gom buổi GV
        addLog(`  Gom buổi GV${roundLabel}...`, 'active');
        const gomRes = await tkbAPI.getAll();
        const gomList = gomRes.data?.success ? gomRes.data.data.filter(t => t.tiet != null) : [];
        const { totalSuccess: gomCount } = await gomBuoiCore(gomList);
        if (gomCount > 0) {
          addLog(`  ✓ Gom buổi: ${gomCount} đổi chỗ`, 'done');
        } else {
          addLog(`  ✓ Lịch buổi đã tối ưu`, 'done');
        }

        // Bước 3: Xếp TKB từng buổi
        addLog(`3. Xếp TKB các buổi${roundLabel}...`, 'step');
        const freshRes = await tkbAPI.getAll();
        const freshList = freshRes.data?.success
          ? freshRes.data.data.filter(t => t.tiet != null)
          : [];
        await scheduleAllCore(freshList, currentFixedCells);

        // Bước 4: Kiểm tra xung đột
        addLog('4. Kiểm tra xung đột...', 'active');
        const checkRes = await tkbAPI.getAll();
        const checkList = checkRes.data?.success
          ? checkRes.data.data.filter(t => t.tiet != null)
          : [];
        const conflicts = countGvConflicts(checkList);

        if (conflicts === 0) {
          addLog('  ✓ Không có xung đột', 'done');
          break;
        }
        if (outer < MAX_OUTER_ROUNDS - 1) {
          addLog(`  ⚠ Còn ${conflicts} tiết xung đột — điều chỉnh lại...`, 'warn');
        } else {
          addLog(`  ⚠ Còn ${conflicts} tiết xung đột sau ${MAX_OUTER_ROUNDS} vòng`, 'warn');
        }
      }

      addLog('─────────────────────────────', 'step');
      addLog('✓ Hoàn tất tạo TKB!', 'done');
      // Chuyển về chế độ xem Màu sắc
      setViewMode('normal');
      setEditMode('view');
    } catch (error) {
      console.error('SpreadUnits error:', error);
      addLog(`✗ Lỗi: ${error.response?.data?.message || 'Có lỗi khi tạo TKB!'}`, 'error');
    } finally {
      setIsAutoScheduling(false);
      const freshTkbs = await fetchAll();
      if (freshTkbs) detectConflictsFromList(freshTkbs);
    }
  };

  // Điều chỉnh buổi dạy - tối ưu phân phối tiết dạy theo buổi cho từng giáo viên
  // Loop cho đến khi nhận được "đã tối ưu" hoặc hết tối đa 20 vòng
  // eslint-disable-next-line no-unused-vars
  const handleAdjustSessions = async () => {
    setIsAutoScheduling(true);
    const MAX_ROUNDS = 20;
    try {
      let lastMsg = '';
      for (let round = 0; round < MAX_ROUNDS; round++) {
        const result = await tkbAPI.adjustTeacherSessions(fixedCells);
        if (!result.data.success) {
          toast.error(result.data.message || 'Có lỗi khi điều chỉnh buổi dạy!');
          break;
        }
        lastMsg = result.data.message || '';
        if (lastMsg.includes('đã tối ưu') || round === MAX_ROUNDS - 1) {
          toast.success(lastMsg);
          break;
        }
        // Còn thay đổi → tiếp tục vòng sau
      }
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Có lỗi khi điều chỉnh buổi dạy!');
    } finally {
      setIsAutoScheduling(false);
    }
  };

  // Pure function: tính danh sách swap có lợi từ một snapshot tkbs bất kỳ
  const computeCompactCandidates = (currentTkbs) => {
    const validTkbs = currentTkbs.filter(t => t.tiet != null && t.tiet >= 1);

    const gvUnits = {};
    for (const t of validTkbs) {
      if (!gvUnits[t.gv_id]) gvUnits[t.gv_id] = [];
      gvUnits[t.gv_id].push(t);
    }

    const gvBusySet = {};
    for (const t of validTkbs) {
      if (!gvBusySet[t.gv_id]) gvBusySet[t.gv_id] = new Set();
      gvBusySet[t.gv_id].add(`${t.thu}-${t.buoi}-${t.tiet}`);
    }

    const calcCost = (gvId, overrideId = null, ovThu = null, ovBuoi = null, ovTiet = null) => {
      const sessMap = {};
      for (const t of (gvUnits[gvId] || [])) {
        let thu = t.thu, buoi = t.buoi, tiet = t.tiet;
        if (overrideId !== null && t.tkb_id === overrideId) { thu = ovThu; buoi = ovBuoi; tiet = ovTiet; }
        const k = `${thu}-${buoi}`;
        if (!sessMap[k]) sessMap[k] = [];
        sessMap[k].push(tiet);
      }
      let gaps = 0;
      for (const ts of Object.values(sessMap)) {
        ts.sort((a, b) => a - b);
        for (let i = 1; i < ts.length; i++) gaps += ts[i] - ts[i - 1] - 1;
      }
      return Object.keys(sessMap).length * 1000 + gaps * 10;
    };

    const calcNumSessions = (gvId, overrideId = null, ovThu = null, ovBuoi = null) => {
      const sessSet = new Set();
      for (const t of (gvUnits[gvId] || [])) {
        let thu = t.thu, buoi = t.buoi;
        if (overrideId !== null && t.tkb_id === overrideId) { thu = ovThu; buoi = ovBuoi; }
        sessSet.add(`${thu}-${buoi}`);
      }
      return sessSet.size;
    };

    const candidates = [];
    const seen = new Set();

    for (const tkbA of validTkbs) {
      if (isFixed(tkbA.lop_id, tkbA.thu, tkbA.buoi, tkbA.tiet)) continue;
      const costA0 = calcCost(tkbA.gv_id);

      for (const tkbB of validTkbs) {
        if (tkbB.tkb_id === tkbA.tkb_id) continue;
        if (String(tkbB.lop_id) !== String(tkbA.lop_id)) continue;
        if (tkbB.thu === tkbA.thu && tkbB.buoi === tkbA.buoi) continue;
        if (String(tkbB.gv_id) === String(tkbA.gv_id)) continue;
        if (isFixed(tkbB.lop_id, tkbB.thu, tkbB.buoi, tkbB.tiet)) continue;

        const pairKey = [tkbA.tkb_id, tkbB.tkb_id].sort().join('-');
        if (seen.has(pairKey)) continue;

        const aFreeAtB = !gvBusySet[tkbA.gv_id]?.has(`${tkbB.thu}-${tkbB.buoi}-${tkbB.tiet}`);
        const bFreeAtA = !gvBusySet[tkbB.gv_id]?.has(`${tkbA.thu}-${tkbA.buoi}-${tkbA.tiet}`);
        if (!aFreeAtB || !bFreeAtA) continue;

        const costB0 = calcCost(tkbB.gv_id);
        const costANew = calcCost(tkbA.gv_id, tkbA.tkb_id, tkbB.thu, tkbB.buoi, tkbB.tiet);
        const costBNew = calcCost(tkbB.gv_id, tkbB.tkb_id, tkbA.thu, tkbA.buoi, tkbA.tiet);
        const delta = (costANew + costBNew) - (costA0 + costB0);

        if (delta >= 0) continue;

        seen.add(pairKey);

        const sessA_before = calcNumSessions(tkbA.gv_id);
        const sessA_after  = calcNumSessions(tkbA.gv_id, tkbA.tkb_id, tkbB.thu, tkbB.buoi);
        const sessB_before = calcNumSessions(tkbB.gv_id);
        const sessB_after  = calcNumSessions(tkbB.gv_id, tkbB.tkb_id, tkbA.thu, tkbA.buoi);
        const deltaSessA = sessA_after - sessA_before;
        const deltaSessB = sessB_after - sessB_before;

        const sessASize = (gvUnits[tkbA.gv_id] || []).filter(
          t => t.thu === tkbA.thu && t.buoi === tkbA.buoi
        ).length;
        const sessBSize = (gvUnits[tkbB.gv_id] || []).filter(
          t => t.thu === tkbB.thu && t.buoi === tkbB.buoi
        ).length;
        const sessAEliminated = deltaSessA < 0 && sessASize === 1;
        const sessBEliminated = deltaSessB < 0 && sessBSize === 1;

        let category;
        if (deltaSessA <= 0 && deltaSessB <= 0 && (deltaSessA < 0 || deltaSessB < 0)) {
          category = 'safe';
        } else if (deltaSessA === 0 && deltaSessB === 0) {
          category = 'gap';
        } else {
          category = 'risky';
        }

        const lop_name = lopHocs.find(l => String(l.lop_id) === String(tkbA.lop_id))?.ten_lop || '';
        candidates.push({
          id: pairKey,
          tkbA, tkbB,
          delta,
          sessA_before, sessA_after, deltaSessA,
          sessB_before, sessB_after, deltaSessB,
          sessAEliminated, sessBEliminated,
          category,
          lop_name,
        });
      }
    }

    const catOrder = { safe: 0, gap: 1, risky: 2 };
    candidates.sort((a, b) => {
      const co = catOrder[a.category] - catOrder[b.category];
      if (co !== 0) return co;
      const elimA = (a.sessAEliminated ? 1 : 0) + (a.sessBEliminated ? 1 : 0);
      const elimB = (b.sessAEliminated ? 1 : 0) + (b.sessBEliminated ? 1 : 0);
      if (elimA !== elimB) return elimB - elimA;
      return a.delta - b.delta;
    });

    return candidates;
  };

  // Thực hiện các đổi chỗ gom buổi đã chọn (từ dialog)
  const handleExecuteCompactSwaps = async () => {
    const { candidates, selected } = compactDialog;
    const toExecute = candidates.filter(c => selected.has(c.id));
    if (toExecute.length === 0) return;

    setCompactDialog(prev => ({ ...prev, executing: true }));
    let successCount = 0;
    let errorCount = 0;

    // currentTkbs theo dõi trạng thái cục bộ để tái phân tích sau mỗi vòng
    let currentTkbs = [...tkbs];
    let currentBatch = toExecute;
    // Chỉ lặp lại các swap "safe" (không ai tăng buổi) để an toàn
    const onlySafe = currentBatch.every(c => c.category === 'safe');

    while (currentBatch.length > 0) {
      for (const swap of currentBatch) {
        const { tkbA, tkbB } = swap;
        // Lấy bản ghi mới nhất từ currentTkbs để tránh dùng data cũ
        const latestA = currentTkbs.find(t => t.tkb_id === tkbA.tkb_id);
        const latestB = currentTkbs.find(t => t.tkb_id === tkbB.tkb_id);
        if (!latestA || !latestB) continue;
        try {
          const [resA, resB] = await Promise.all([
            tkbAPI.update(latestA.tkb_id, {
              gvId: latestB.gv_id, monId: latestB.mon_id, lopId: latestA.lop_id,
              thu: latestA.thu, tiet: latestA.tiet, buoi: latestA.buoi,
              soTiet: Math.max(1, Number(latestB.so_tiet || 1)),
              phong: latestB.phong || null, ghiChu: latestB.ghi_chu || null,
              skipOverlapCheck: true,
            }),
            tkbAPI.update(latestB.tkb_id, {
              gvId: latestA.gv_id, monId: latestA.mon_id, lopId: latestB.lop_id,
              thu: latestB.thu, tiet: latestB.tiet, buoi: latestB.buoi,
              soTiet: Math.max(1, Number(latestA.so_tiet || 1)),
              phong: latestA.phong || null, ghiChu: latestA.ghi_chu || null,
              skipOverlapCheck: true,
            }),
          ]);
          const updA = decorateTkb(resA.data?.data || resA.data);
          const updB = decorateTkb(resB.data?.data || resB.data);
          applyUpdatedTkbs([updA, updB]);
          // Cập nhật snapshot cục bộ
          currentTkbs = currentTkbs.map(t => {
            if (t.tkb_id === updA.tkb_id) return updA;
            if (t.tkb_id === updB.tkb_id) return updB;
            return t;
          });
          successCount++;
        } catch (err) {
          errorCount++;
          console.error('Compact swap error:', err);
        }
      }

      // Tái phân tích với trạng thái mới; chỉ tiếp tục nếu ban đầu chọn "safe"
      if (!onlySafe) break;
      const nextCandidates = computeCompactCandidates(currentTkbs);
      currentBatch = nextCandidates.filter(c => c.category === 'safe');
    }

    setCompactDialog(prev => ({ ...prev, open: false, executing: false }));
    if (successCount > 0) toast.success(`Đã thực hiện ${successCount} đổi chỗ gom buổi thành công!`);
    if (errorCount > 0) toast.error(`${errorCount} đổi chỗ thất bại.`);
    fetchAll();
  };

  // Core gom buổi — dùng chung cho nút và bước trong Tạo TKB
  // Trả về số swap thực hiện được
  const gomBuoiCore = async (initialTkbs) => {
    let currentTkbs = [...initialTkbs];
    let totalSuccess = 0;
    let maxIter = 200;
    while (maxIter-- > 0) {
      const safeCandidates = computeCompactCandidates(currentTkbs)
        .filter(c => c.category === 'safe');
      if (safeCandidates.length === 0) break;
      const usedGVs = new Set();
      const thisBatch = [];
      for (const swap of safeCandidates) {
        const gvA = String(swap.tkbA.gv_id), gvB = String(swap.tkbB.gv_id);
        if (usedGVs.has(gvA) || usedGVs.has(gvB)) continue;
        usedGVs.add(gvA); usedGVs.add(gvB);
        thisBatch.push(swap);
      }
      if (thisBatch.length === 0) break;
      for (const swap of thisBatch) {
        const latestA = currentTkbs.find(t => t.tkb_id === swap.tkbA.tkb_id);
        const latestB = currentTkbs.find(t => t.tkb_id === swap.tkbB.tkb_id);
        if (!latestA || !latestB) continue;
        try {
          const [resA, resB] = await Promise.all([
            tkbAPI.update(latestA.tkb_id, {
              gvId: latestB.gv_id, monId: latestB.mon_id, lopId: latestA.lop_id,
              thu: latestA.thu, tiet: latestA.tiet, buoi: latestA.buoi,
              soTiet: Math.max(1, Number(latestB.so_tiet || 1)),
              phong: latestB.phong || null, ghiChu: latestB.ghi_chu || null,
              skipOverlapCheck: true,
            }),
            tkbAPI.update(latestB.tkb_id, {
              gvId: latestA.gv_id, monId: latestA.mon_id, lopId: latestB.lop_id,
              thu: latestB.thu, tiet: latestB.tiet, buoi: latestB.buoi,
              soTiet: Math.max(1, Number(latestA.so_tiet || 1)),
              phong: latestA.phong || null, ghiChu: latestA.ghi_chu || null,
              skipOverlapCheck: true,
            }),
          ]);
          const updA = decorateTkb(resA.data?.data || resA.data);
          const updB = decorateTkb(resB.data?.data || resB.data);
          applyUpdatedTkbs([updA, updB]);
          currentTkbs = currentTkbs.map(t => {
            if (t.tkb_id === updA.tkb_id) return updA;
            if (t.tkb_id === updB.tkb_id) return updB;
            return t;
          });
          totalSuccess++;
        } catch (err) {
          console.error('Gom buoi error:', err);
        }
      }
    }
    return { totalSuccess, updatedTkbs: currentTkbs };
  };

  // Chuyển tất cả đơn vị TKB ở hàng Tạm lên buổi phù hợp
  // eslint-disable-next-line no-unused-vars
  const handleScheduleOverflow = async () => {
    const hasOverflow = tkbs.some(t =>
      t.thu < 2 || t.thu > 7 || !['Sang', 'Chieu'].includes(t.buoi) || t.tiet < 1 || t.tiet > 5
    );
    if (!hasOverflow && unassignedUnits.length === 0) {
      toast.info('Không có tiết nào ở hàng Tạm!');
      return;
    }
    setIsAutoScheduling(true);
    try {
      const result = await tkbAPI.scheduleOverflow([], blockedCells, fixedCells);
      if (result.data.success) {
        const { stillUnassigned } = result.data.data || {};
        if (stillUnassigned && stillUnassigned.length > 0) {
          setUnassignedUnits(stillUnassigned);
        } else {
          setUnassignedUnits([]);
        }
        toast.success(result.data.message);
        fetchAll();
      } else {
        toast.error(result.data.message || 'Có lỗi khi chuyển tiết từ Tạm!');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Có lỗi khi chuyển tiết từ Tạm!');
    } finally {
      setIsAutoScheduling(false);
    }
  };

  // Thực hiện chuyển đơn vị TKB của GV vượt tiết sang buổi khác
  const handleOverloadMove = async () => {
    const { selectedUnit, selectedSlot } = overloadMoveDialog;
    if (!selectedUnit || !selectedSlot) return;
    try {
      const res = await tkbAPI.update(selectedUnit.tkb_id, buildUpdatePayload(selectedUnit, selectedSlot));
      const updated = decorateTkb(res.data?.data || res.data);
      if (updated?.tkb_id) applyUpdatedTkbs([updated]);
      setOverloadMoveDialog(prev => ({ ...prev, open: false }));
      toast.success(`Đã chuyển ${selectedUnit.ten_mon} - ${selectedUnit.ten_lop} → ${selectedSlot.thuLabel} ${selectedSlot.buoiLabel} Tiết ${selectedSlot.tiet}`);
    } catch (err) {
      toast.error('Có lỗi khi chuyển tiết!');
    }
  };

  // Xếp TKB trong 1 buổi cụ thể - thực hiện ngay với buổi đang được chọn trên bảng
  const handleScheduleSession = async () => {
    if (!activeSession.thu || !activeSession.buoi) {
      toast.warning('Vui lòng click vào 1 ô trong bảng để chọn buổi cần xếp trước!');
      return;
    }

    // Kiểm tra GV vượt số tiết có thể xếp trong buổi
    const sessionTkbs = tkbs.filter(t =>
      Number(t.thu) === Number(activeSession.thu) && t.buoi === activeSession.buoi
    );
    const gvTotals = {};
    const gvNames = {};
    const gvLops = {};  // gv_id → Set<lop_id>
    sessionTkbs.forEach(t => {
      const soTiet = Math.max(1, Number(t.so_tiet || 1));
      gvTotals[t.gv_id] = (gvTotals[t.gv_id] || 0) + soTiet;
      gvNames[t.gv_id] = t.ten_gv;
      if (!gvLops[t.gv_id]) gvLops[t.gv_id] = new Set();
      gvLops[t.gv_id].add(t.lop_id);
    });
    // Số tiết tối đa cho GV = số tiet distinct bị block cho bất kỳ lớp nào họ dạy trong buổi này
    const hasAnyBlocked = Object.keys(blockedCells).length > 0;
    const gvMaxTiets = {};
    Object.entries(gvLops).forEach(([gvId, lopSet]) => {
      if (!hasAnyBlocked) { gvMaxTiets[gvId] = 5; return; }
      const availTiets = new Set();
      lopSet.forEach(lopId => {
        for (let tiet = 1; tiet <= 10; tiet++) {
          if (blockedCells[`${lopId}-${activeSession.thu}-${activeSession.buoi}-${tiet}`]) availTiets.add(tiet);
        }
      });
      gvMaxTiets[gvId] = availTiets.size;
    });
    const overloadedGvs = Object.keys(gvTotals).filter(id => gvTotals[id] > (gvMaxTiets[id] ?? 5));
    if (overloadedGvs.length > 0) {
      const names = overloadedGvs.map(id => `${gvNames[id]} (${gvTotals[id]}/${gvMaxTiets[id]} tiết)`).join(', ');
      toast.warning(`GV vượt số tiết trong buổi (sẽ bỏ qua): ${names}`);
      // Highlight GV đầu tiên bị vượt — làm nổi bật cell của họ trên bảng
      setHighlightGvId(String(overloadedGvs[0]));
      // Các GV vượt còn lại: tô màu lên cột Tiết các tiet họ đang dạy trong buổi này
      if (overloadedGvs.length > 1) {
        const overloadSet = new Set(overloadedGvs.slice(1));
        setSwapConflictHighlights(prev => {
          const next = { ...prev };
          sessionTkbs.forEach(t => {
            if (!overloadSet.has(String(t.gv_id)) || !t.tiet) return;
            const key = `${t.thu}-${t.buoi}-${t.tiet}`;
            if (!next[key]) next[key] = { color: getGvColor(t.gv_id), gvId: t.gv_id };
          });
          return next;
        });
      }
      // Mở dialog chuyển tiết cho GV đầu tiên bị vượt
      const firstId = overloadedGvs[0];
      const gvUnits = sessionTkbs.filter(t =>
        String(t.gv_id) === String(firstId) && !isFixed(t.lop_id, t.thu, t.buoi, t.tiet)
      );
      const firstUnit = gvUnits[0] || null;
      setOverloadMoveDialog({
        open: true,
        gvId: firstId, gvName: gvNames[firstId],
        soTiet: gvTotals[firstId], maxTiet: gvMaxTiets[firstId] ?? 5,
        sourceThu: activeSession.thu, sourceBuoi: activeSession.buoi,
        units: gvUnits, selectedUnit: firstUnit,
        suggestedSlots: firstUnit ? findSuggestedSlotsForUnit(firstUnit, activeSession.thu, activeSession.buoi) : [],
        selectedSlot: null,
      });
    }

    setIsAutoScheduling(true);
    const MAX_RETRY = 10;
    let currentTkbs = [...tkbs]; // Bản sao local để cập nhật giữa các lần retry

    // Helper: tìm TẤT CẢ các units bị trùng GV trong updatedRecords (1 unit/nhóm xung đột, dedup theo tkb_id)
    const findAllConflictUnitsToMove = (records) => {
      const gvTietMap = {};
      for (const r of records) {
        if (r.tiet == null) continue;
        const soTiet = Math.max(1, Number(r.so_tiet || 1));
        for (let i = 0; i < soTiet; i++) {
          const k = `${r.gv_id}|${r.tiet + i}`;
          if (!gvTietMap[k]) gvTietMap[k] = [];
          gvTietMap[k].push(r);
        }
      }
      const toMove = new Map(); // tkb_id → unit
      for (const units of Object.values(gvTietMap)) {
        if (units.length <= 1) continue;
        // Lấy 1 unit di chuyển được từ nhóm xung đột này
        const movable = units.filter(u => {
          const soTiet = Math.max(1, Number(u.so_tiet || 1));
          for (let i = 0; i < soTiet; i++) {
            if (isFixed(u.lop_id, activeSession.thu, activeSession.buoi, u.tiet + i)) return false;
          }
          return true;
        });
        if (movable.length > 0 && !toMove.has(movable[0].tkb_id)) {
          toMove.set(movable[0].tkb_id, movable[0]);
        }
      }
      return [...toMove.values()];
    };

    // Helper: tìm slot khác buổi có thể chứa unit này (dùng currentTkbs thay tkbs)
    const findSlotForUnit = (unit) => {
      const { lop_id, gv_id } = unit;
      const soTiet = Math.max(1, Number(unit.so_tiet || 1));
      const hasAnyBlocked = Object.keys(blockedCells).length > 0;
      for (const thu of THU_LIST) {
        for (const buoi of BUOI_LIST) {
          if (Number(thu.value) === Number(activeSession.thu) && buoi.value === activeSession.buoi) continue;
          for (let tiet = 1; tiet + soTiet - 1 <= 5; tiet++) {
            let ok = true;
            for (let offset = 0; offset < soTiet; offset++) {
              const p = tiet + offset;
              const cellKey = `${lop_id}-${thu.value}-${buoi.value}-${p}`;
              if (hasAnyBlocked && !blockedCells[cellKey]) { ok = false; break; }
              if (isFixed(lop_id, thu.value, buoi.value, p)) { ok = false; break; }
              const occupied = currentTkbs.some(t => {
                if (t.lop_id !== lop_id || Number(t.thu) !== Number(thu.value) || t.buoi !== buoi.value) return false;
                const st = Math.max(1, Number(t.so_tiet || 1));
                return p >= Number(t.tiet) && p <= Number(t.tiet) + st - 1;
              });
              if (occupied) { ok = false; break; }
              const gvBusy = currentTkbs.some(t =>
                String(t.gv_id) === String(gv_id) && Number(t.thu) === Number(thu.value) &&
                t.buoi === buoi.value &&
                p >= Number(t.tiet) && p <= Number(t.tiet) + Math.max(1, Number(t.so_tiet || 1)) - 1
              );
              if (gvBusy) { ok = false; break; }
            }
            if (ok) return { thu: thu.value, buoi: buoi.value, tiet };
          }
        }
      }
      return null;
    };

    const applySuccess = (result) => {
      const [mainMsg, ...warnParts] = (result.data.message || '').split('\n');
      toast.success(mainMsg);
      if (warnParts.length > 0) toast.warning(warnParts.join('\n'));
      const records = result.data.data?.updatedRecords;
      const decorated = records?.length ? records.map(decorateTkb) : [];
      if (decorated.length) applyUpdatedTkbs(decorated);
      const updatedMap = new Map(decorated.map(t => [t.tkb_id, t]));
      const mergedTkbs = tkbs.map(t => updatedMap.has(t.tkb_id) ? { ...t, ...updatedMap.get(t.tkb_id) } : t);
      const conflictMap = {};
      const slots = new Set(mergedTkbs.map(t => `${t.thu}-${t.buoi}-${t.tiet}`));
      slots.forEach(slotKey => {
        const [thu2, buoi2, tiet2] = slotKey.split('-').map((v, i) => i === 0 || i === 2 ? Number(v) : v);
        const c = getConflictColorForSlot(mergedTkbs, { thu: thu2, buoi: buoi2, tiet: tiet2 });
        if (c) conflictMap[slotKey] = c;
      });
      setSwapConflictHighlights(conflictMap);
    };

    try {
      for (let attempt = 0; attempt <= MAX_RETRY; attempt++) {
        let result, failed = false, failedRecords = [], failedMsg = '';
        try {
          result = await tkbAPI.scheduleSessionBacktrack(activeSession.thu, activeSession.buoi, blockedCells, fixedCells);
        } catch (err) {
          // 400 = không xếp được, data vẫn có updatedRecords
          failed = true;
          failedMsg = err.response?.data?.message || 'Không thể xếp lịch buổi này!';
          failedRecords = err.response?.data?.data?.updatedRecords || [];
        }

        if (!failed) {
          applySuccess(result);
          break;
        }

        // Đã thất bại - thử phá vòng lặp bằng cách chuyển TẤT CẢ units xung đột
        if (attempt === MAX_RETRY) {
          toast.error(failedMsg + ' (đã thử ' + MAX_RETRY + ' lần)');
          break;
        }

        const unitsToMove = findAllConflictUnitsToMove(failedRecords);
        if (unitsToMove.length === 0) {
          toast.error(failedMsg);
          break;
        }

        // Helper: tìm đơn vị ở buổi khác để hoán đổi GV (reiO style)
        // eslint-disable-next-line no-loop-func
        const findAutoReiOTarget = (unit) => {
          const srcGvId = String(unit.gv_id);
          const srcSoTiet = Math.max(1, Number(unit.so_tiet || 1));
          for (const tgt of currentTkbs) {
            if (Number(tgt.thu) === Number(activeSession.thu) && tgt.buoi === activeSession.buoi) continue;
            if (!tgt.gv_id || tgt.tiet == null) continue;
            if (isFixed(tgt.lop_id, tgt.thu, tgt.buoi, tgt.tiet)) continue;
            if (String(tgt.gv_id) === srcGvId) continue;
            const tgtGvId = String(tgt.gv_id);
            const tgtSoTiet = Math.max(1, Number(tgt.so_tiet || 1));
            let srcBusy = false;
            for (let i = 0; i < srcSoTiet && !srcBusy; i++) {
              const p = Number(tgt.tiet) + i;
              if (currentTkbs.some(t =>
                t.tkb_id !== tgt.tkb_id && String(t.gv_id) === srcGvId &&
                Number(t.thu) === Number(tgt.thu) && t.buoi === tgt.buoi &&
                p >= Number(t.tiet) && p <= Number(t.tiet) + Math.max(1, Number(t.so_tiet || 1)) - 1
              )) srcBusy = true;
            }
            if (srcBusy) continue;
            let tgtBusy = false;
            for (let i = 0; i < tgtSoTiet && !tgtBusy; i++) {
              const p = Number(unit.tiet) + i;
              if (currentTkbs.some(t =>
                t.tkb_id !== unit.tkb_id && String(t.gv_id) === tgtGvId &&
                Number(t.thu) === Number(activeSession.thu) && t.buoi === activeSession.buoi &&
                p >= Number(t.tiet) && p <= Number(t.tiet) + Math.max(1, Number(t.so_tiet || 1)) - 1
              )) tgtBusy = true;
            }
            if (tgtBusy) continue;
            return tgt;
          }
          return null;
        };

        // Tìm slot và chuyển từng unit, cập nhật currentTkbs ngay để tránh chọn lại slot đã dùng
        let anyMoved = false;
        for (const unit of unitsToMove) {
          const targetSlot = findSlotForUnit(unit);
          if (targetSlot) {
            await tkbAPI.update(unit.tkb_id, buildUpdatePayload(unit, targetSlot));
            currentTkbs = currentTkbs.map(t =>
              t.tkb_id === unit.tkb_id
                ? { ...t, thu: targetSlot.thu, buoi: targetSlot.buoi, tiet: targetSlot.tiet }
                : t
            );
            anyMoved = true;
            continue;
          }
          // Fallback: hoán đổi nội dung GV (reiO style) khi không có ô trống
          const swapTarget = findAutoReiOTarget(unit);
          if (swapTarget) {
            await Promise.all([
              tkbAPI.update(unit.tkb_id, {
                gvId: swapTarget.gv_id, monId: swapTarget.mon_id, lopId: unit.lop_id,
                thu: Number(activeSession.thu), tiet: unit.tiet,
                soTiet: Math.max(1, Number(swapTarget.so_tiet || 1)),
                buoi: activeSession.buoi, skipOverlapCheck: true,
              }),
              tkbAPI.update(swapTarget.tkb_id, {
                gvId: unit.gv_id, monId: unit.mon_id, lopId: swapTarget.lop_id,
                thu: Number(swapTarget.thu), tiet: swapTarget.tiet,
                soTiet: Math.max(1, Number(unit.so_tiet || 1)),
                buoi: swapTarget.buoi, skipOverlapCheck: true,
              }),
            ]);
            currentTkbs = currentTkbs.map(t => {
              if (t.tkb_id === unit.tkb_id) return { ...t, gv_id: swapTarget.gv_id, mon_id: swapTarget.mon_id };
              if (t.tkb_id === swapTarget.tkb_id) return { ...t, gv_id: unit.gv_id, mon_id: unit.mon_id };
              return t;
            });
            anyMoved = true;
          }
        }
        if (!anyMoved) {
          toast.error('Không thể xếp: không còn buổi trống để chuyển tiết bị xung đột.');
          break;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Có lỗi khi xếp lịch buổi!');
    } finally {
      setIsAutoScheduling(false);
      const snapThu = activeSession.thu;
      const snapBuoi = activeSession.buoi;
      const freshTkbs = await fetchAll();
      if (freshTkbs && snapThu && snapBuoi) {
        const sessionTkbs = freshTkbs.filter(t =>
          Number(t.thu) === Number(snapThu) && t.buoi === snapBuoi
        );
        detectConflictsFromList(sessionTkbs);
      }
    }
  };

  // Xếp TKB tất cả các buổi tuần tự
  // eslint-disable-next-line no-unused-vars
  const handleScheduleAllSessions = async () => {
    setIsAutoScheduling(true);
    try {
      await scheduleAllCore([...tkbs]);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Có lỗi khi xếp TKB các buổi!');
    } finally {
      setIsAutoScheduling(false);
      const freshTkbs = await fetchAll();
      if (freshTkbs) detectConflictsFromList(freshTkbs);
    }
  };

  // Lấy tên viết tắt môn học
  const getMonShortName = (tenMon) => {
    if (!tenMon) return '';
    if (tenMon.length <= 4) return tenMon;
    // Lấy chữ cái đầu của mỗi từ
    return tenMon.split(' ').map(w => w.charAt(0)).join('').toUpperCase();
  };

  // Lấy màu nền theo giáo viên (mỗi gv_id một màu)
  const getGvColor = (gvId) => {
    if (!gvId) return 'transparent';
    
    // Mảng màu pastel dễ nhìn
    const colors = [
      '#ffeaa7', // vàng nhạt
      '#fdcb6e', // vàng cam
      '#e17055', // cam đỏ
      '#d63031', // đỏ
      '#e84393', // hồng
      '#fd79a8', // hồng nhạt
      '#a29bfe', // tím nhạt
      '#6c5ce7', // tím
      '#74b9ff', // xanh dương nhạt
      '#0984e3', // xanh dương
      '#00b894', // xanh lá nhạt
      '#00cec9', // xanh ngọc
      '#55a3ff', // xanh dương khác
      '#fd79a8', // hồng khác
      '#e84393', // hồng đậm
      '#d63031', // đỏ đậm
      '#e17055', // cam
      '#fdcb6e', // vàng
      '#ffeaa7', // vàng nhạt
      '#a29bfe'  // tím
    ];
    
    // Sử dụng hash của gv_id để chọn màu nhất quán
    let hash = 0;
    const str = gvId.toString();
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Chọn màu chữ tương phản với màu nền (đen/trắng)
  const getContrastTextColor = (bgColor) => {
    if (!bgColor || bgColor === 'transparent') return '#111';
    const hex = bgColor.replace('#', '').trim();
    if (hex.length !== 6) return '#111';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return '#111';

    // Relative luminance (W3C-ish)
    const srgb = [r, g, b].map(v => v / 255);
    const lin = srgb.map(c => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
    const L = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];

    // Nền tối => chữ trắng, nền sáng => chữ đen
    return L < 0.5 ? '#fff' : '#111';
  };

  const getConflictColorForSlot = (tkbList, slot, candidateGvIds = []) => {
    const sameSlot = tkbList.filter(t =>
      Number(t.thu) === Number(slot.thu) &&
      String(t.buoi) === String(slot.buoi) &&
      Number(t.tiet) === Number(slot.tiet)
    );
    const counts = {};
    sameSlot.forEach(t => {
      if (!t.gv_id) return;
      const key = String(t.gv_id);
      counts[key] = (counts[key] || 0) + 1;
    });
    const candidates = candidateGvIds.length ? candidateGvIds.map(String) : Object.keys(counts);
    const conflictGvId = candidates.find(id => counts[id] > 1);
    return conflictGvId ? { color: getGvColor(conflictGvId), gvId: conflictGvId } : null;
  };

  const getConflictColorForThu = (thu) => {
    const prefix = `${thu}-`;
    const key = Object.keys(swapConflictHighlights).find(k => k.startsWith(prefix));
    return key ? swapConflictHighlights[key] : null;
  };

  // Handler: Chuyển tab toolbar — tự động bật/tắt editMode block khi vào/rời "Vùng xếp"
  const handleTabChange = (tab) => {
    const next = activeToolTab === tab ? null : tab;
    if (next === 'vung-xep') {
      prevViewModeRef.current = viewMode;
      setViewMode('showBlocked');
      setEditMode('block');
      setSwapEnabled(false);
      setSwapSource(null);
    } else if (activeToolTab === 'vung-xep') {
      setViewMode(prevViewModeRef.current);
      setEditMode('view');
    }
    setActiveToolTab(next);
  };
  
  // Handler: Sao đánh dấu từ lớp khác
  const handleCopyBlockedFromClass = async () => {
    if (!sourceLopForCopy || selectedTargetLops.length === 0) {
      toast.warning('Vui lòng chọn lớp nguồn và ít nhất một lớp đích!');
      return;
    }
    
    if (selectedTargetLops.includes(sourceLopForCopy)) {
      toast.warning('Lớp nguồn không được nằm trong danh sách lớp đích!');
      return;
    }
    
    try {
      // Lấy tất cả blocked cells từ database
      const res = await blockedCellAPI.getAll();
      if (res.data.success) {
        // Lọc các cells của lớp nguồn
        const sourceCells = res.data.data.filter(cell => 
          String(cell.lop_id) === String(sourceLopForCopy)
        );
        
        if (sourceCells.length === 0) {
          toast.warning('Lớp nguồn chưa có đánh dấu nào!');
          return;
        }
        
        // Gộp tất cả cells cho mọi lớp đích thành 1 request duy nhất
        const allCells = selectedTargetLops.flatMap(targetLopId =>
          sourceCells.map(cell => ({
            lop_id: targetLopId,
            thu: cell.thu,
            buoi: cell.buoi,
            tiet: cell.tiet,
            ghi_chu: cell.ghi_chu
          }))
        );

        const batchRes = await blockedCellAPI.createBatch(allCells);
        const { inserted = 0, skipped = 0 } = batchRes.data.data || {};

        // Reload lại blocked cells
        await fetchAll();

        if (inserted > 0) {
          toast.success(`Đã sao ${inserted} đánh dấu sang ${selectedTargetLops.length} lớp!${skipped > 0 ? ` (${skipped} ô đã tồn tại)` : ''}`);
        } else if (skipped > 0) {
          toast.warning('Tất cả các ô này đã được đánh dấu ở các lớp đích!');
        }
        
        setCopyBlockedDialogOpen(false);
        setSelectedTargetLops([]);
      }
    } catch (error) {
      toast.error('Lỗi khi sao đánh dấu!');
      console.error(error);
    }
  };
  
  // Handler: Xóa toàn bộ đánh dấu (chỉ xóa blocked cells, không xóa fixed cells)
  const handleClearAllBlocked = async () => {
    if (Object.keys(blockedCells).length === 0) {
      toast.info('Chưa có đánh dấu nào để xóa');
      return;
    }
    
    try {
      await blockedCellAPI.deleteAll();
      setBlockedCells({});
      blockedCellsRef.current = {};
      toast.success('Đã xóa toàn bộ đánh dấu sẽ học!');
      setBlockedMenuOpen(false);
    } catch (error) {
      toast.error('Lỗi khi xóa đánh dấu!');
      console.error(error);
    }
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);
      const cells = rows
        .map(r => {
          const tenLop = r['Lớp'] || r['Lop'] || r['lop'];
          const lop = lopHocs.find(l => l.ten_lop === String(tenLop).trim());
          return {
            lop_id: lop?.lop_id || null,
            thu: Number(r['Thứ'] ?? r['Thu'] ?? r['thu']),
            buoi: String(r['Buổi'] ?? r['Buoi'] ?? r['buoi'] ?? '').trim(),
            tiet: Number(r['Tiết'] ?? r['Tiet'] ?? r['tiet']),
            ghi_chu: r['Ghi chú'] || r['Ghi chu'] || null
          };
        })
        .filter(c => c.thu && c.buoi && c.tiet);
      if (cells.length === 0) return toast.error('Không có dữ liệu hợp lệ trong file!');
      const CHUNK = 1000;
      let totalInserted = 0, totalSkipped = 0;
      for (let i = 0; i < cells.length; i += CHUNK) {
        const res = await blockedCellAPI.createBatch(cells.slice(i, i + CHUNK));
        const { inserted = 0, skipped = 0 } = res.data?.data || {};
        totalInserted += inserted;
        totalSkipped += skipped;
      }
      await fetchAll();
      toast.success(`Đã import ${totalInserted} ô, bỏ qua ${totalSkipped} ô trùng!`);
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      toast.error('Lỗi import: ' + msg);
    }
  };

  // Handler: Tự động tạo vùng xếp TKB dựa trên phân công môn học
  const handleAutoGenerateVungXep = async () => {
    if (phanCongs.length === 0) {
      toast.warning('Chưa có phân công môn học nào!');
      return;
    }
    if (lopHocs.length === 0) {
      toast.warning('Chưa có lớp học nào!');
      return;
    }

    // Fisher-Yates shuffle
    const shuffle = (arr) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    const allCells = [];

    // Bước 1: Tính totalTiet mỗi lớp
    const lopTotalTiet = {};
    for (const lop of lopHocs) {
      const lopPhanCongs = phanCongs.filter(pc => String(pc.lop_id) === String(lop.lop_id));
      lopTotalTiet[lop.lop_id] = lopPhanCongs.reduce((sum, pc) => {
        const mon = monHocs.find(m => String(m.mon_id) === String(pc.mon_id));
        return sum + (Number(mon?.so_tiet_tuan ?? pc.so_tiet_tuan) || 0);
      }, 0);
    }

    const validLops = lopHocs.filter(l => (lopTotalTiet[l.lop_id] || 0) > 0);
    if (validLops.length === 0) {
      toast.warning('Không có dữ liệu phân công hợp lệ để tạo vùng xếp!');
      return;
    }

    // Bước 2: Tính N_max để quyết định có dùng T7 không
    // Chỉ dùng T7 khi số buổi cần > 10 (T2-T6 × Sáng+Chiều = 10 buổi đã kín)
    const N_max = Math.max(...validLops.map(l => Math.ceil(lopTotalTiet[l.lop_id] / 5)));
    const useT7 = N_max > 10;

    // Bước 3: Nhóm lớp theo khối, lưu kèm buoi_hoc
    const lopBuoiMap = {}; // lop_id → 'Sang' | 'Chieu' | null
    for (const lop of validLops) lopBuoiMap[lop.lop_id] = lop.buoi_hoc || null;

    const khoiMap = new Map(); // khoiId → [lop_id, ...]
    for (const lop of validLops) {
      const khoiId = lop.khoi_id != null ? lop.khoi_id : `solo_${lop.lop_id}`;
      if (!khoiMap.has(khoiId)) khoiMap.set(khoiId, []);
      khoiMap.get(khoiId).push(lop.lop_id);
    }

    // Bước 4: Sinh vùng xếp từng khối
    for (const lopIds of khoiMap.values()) {
      const numLops = lopIds.length;

      // T3-T6 shuffle ngẫu nhiên riêng cho từng khối (T2 luôn có — Chào cờ/Sinh hoạt)
      const otherDays = shuffle([3, 4, 5, 6]);
      if (useT7) otherDays.push(...shuffle([7]));
      const numOtherDays = otherDays.length;

      lopIds.forEach((lopId, classIdx) => {
        const totalTiet = lopTotalTiet[lopId];
        const N = Math.ceil(totalTiet / 5);

        // Buổi ưu tiên: theo buoi_hoc đã phân; nếu chưa xác định thì xen kẽ trong khối
        const fixedBuoi = lopBuoiMap[lopId]; // 'Sang' | 'Chieu' | null
        const prefBuoi = fixedBuoi || ((classIdx % 2 === 0) ? 'Sang' : 'Chieu');
        const otherBuoi = prefBuoi === 'Sang' ? 'Chieu' : 'Sang';

        // T2 bắt buộc (Chào cờ / Sinh hoạt) — luôn là buổi ưu tiên của lớp
        const forcedSlot = { thu: 2, buoi: prefBuoi };

        // Offset ngày cho T3-T6: dàn đều các lớp trong cùng khối
        const dayOffset = Math.round((classIdx / numLops) * numOtherDays) % numOtherDays;
        const rotatedOtherDays = [...otherDays.slice(dayOffset), ...otherDays.slice(0, dayOffset)];

        // Thứ tự ưu tiên slot (trừ T2): pref trước, other sau, cuối cùng T2-other (cực hiếm dùng)
        const prefOther = rotatedOtherDays.map(thu => ({ thu, buoi: prefBuoi }));
        const otherOther = rotatedOtherDays.map(thu => ({ thu, buoi: otherBuoi }));
        const t2Other = { thu: 2, buoi: otherBuoi };
        const orderedOtherSlots = [...prefOther, ...otherOther, t2Other];

        // T2 bắt buộc + N-1 slot còn lại (pref ca trước, 2 ca chỉ khi N > numOtherDays+1)
        const remainingSlots = orderedOtherSlots.slice(0, Math.max(0, N - 1));
        const selectedSlots = [forcedSlot, ...remainingSlots];

        // Phân bổ tiết cân bằng (chênh lệch tối đa 1 tiết giữa các buổi)
        const baseTiet = Math.floor(totalTiet / N);
        const extra = totalTiet % N;
        const shuffledSlots = shuffle(selectedSlots);
        shuffledSlots.forEach((s, i) => {
          s.tiets = baseTiet + (i < extra ? 1 : 0);
        });

        for (const { thu, buoi, tiets } of shuffledSlots) {
          for (let t = 1; t <= tiets; t++) {
            allCells.push({ lop_id: lopId, thu, buoi, tiet: t });
          }
        }
      });
    }

    if (allCells.length === 0) {
      toast.warning('Không có dữ liệu phân công hợp lệ để tạo vùng xếp!');
      return;
    }

    try {
      // Xóa vùng xếp cũ trước khi tạo mới
      await blockedCellAPI.deleteAll();

      const CHUNK = 500;
      let totalInserted = 0;
      for (let i = 0; i < allCells.length; i += CHUNK) {
        const res = await blockedCellAPI.createBatch(allCells.slice(i, i + CHUNK));
        totalInserted += res.data?.data?.inserted || 0;
      }

      await fetchAll();
      // Chuyển về chế độ xem Vùng xếp
      prevViewModeRef.current = viewMode;
      setViewMode('showBlocked');
      setEditMode('block');
      toast.success(`Đã tạo vùng xếp tự động: ${totalInserted} ô cho ${lopHocs.length} lớp!`);
    } catch (error) {
      toast.error('Lỗi khi tạo vùng xếp tự động!');
      console.error(error);
    }
  };

  const handleClearAllFixed = async () => {
    if (Object.keys(fixedCells).length === 0) {
      toast.info('Chưa có ô cố định nào để xóa');
      return;
    }

    try {
      await fixedCellAPI.deleteAll();
      setFixedCells({});
      toast.success('Đã xóa toàn bộ ô cố định!');
      setFixedMenuOpen(false);
    } catch (error) {
      toast.error('Lỗi khi xóa ô cố định!');
      console.error(error);
    }
  };

  const normalizeId = (value) => (value == null ? '' : String(value));

  const sortedMonHocs = useMemo(() => {
    return [...monHocs].sort((a, b) => {
      const thuTuA = Number(a.thu_tu || 0);
      const thuTuB = Number(b.thu_tu || 0);
      if (thuTuA !== thuTuB) return thuTuA - thuTuB;
      return (a.ten_mon || '').localeCompare(b.ten_mon || '', 'vi');
    });
  }, [monHocs]);

  const monNameById = useMemo(() => {
    const map = new Map();
    monHocs.forEach(mon => {
      const key = normalizeId(mon.mon_id);
      const name = (mon.ten_mon || '').trim().toLowerCase();
      if (key && name) map.set(key, name);
    });
    return map;
  }, [monHocs]);

  const phanCongMap = useMemo(() => {
    const map = new Set();
    phanCongs.forEach(pc => {
      const lopKey = normalizeId(pc?.lop_id);
      const monKey = normalizeId(pc?.mon_id);
      if (lopKey && monKey) {
        map.add(`${lopKey}-${monKey}`);
      }
    });
    return map;
  }, [phanCongs]);

  const tkbMap = useMemo(() => {
    const map = new Set();
    tkbs.forEach(t => {
      const lopKey = normalizeId(t?.lop_id);
      const monKey = normalizeId(t?.mon_id);
      if (lopKey && monKey) {
        map.add(`${lopKey}-${monKey}`);
      }
    });
    return map;
  }, [tkbs]);

  const phanCongByLopAndMonName = useMemo(() => {
    const map = new Map();
    phanCongs.forEach(pc => {
      const lopKey = normalizeId(pc?.lop_id);
      const name = (pc?.ten_mon || '').trim().toLowerCase();
      if (lopKey && name) {
        map.set(`${lopKey}-${name}`, pc);
      }
    });
    return map;
  }, [phanCongs]);

  const tkbByLopAndMonName = useMemo(() => {
    const map = new Map();
    tkbs.forEach(t => {
      const lopKey = normalizeId(t?.lop_id);
      const name = (t?.ten_mon || '').trim().toLowerCase();
      if (lopKey && name) {
        const key = `${lopKey}-${name}`;
        if (!map.has(key)) map.set(key, t);
      }
    });
    return map;
  }, [tkbs]);

  const findPhanCong = (lopId, monId) => {
    const lopKey = normalizeId(lopId);
    const monKey = normalizeId(monId);
    return phanCongs.find(pc => normalizeId(pc.lop_id) === lopKey && normalizeId(pc.mon_id) === monKey);
  };

  const resolveMonIdForLop = (lopId, monId) => {
    const lopKey = normalizeId(lopId);
    const monKey = normalizeId(monId);
    if (!lopKey || !monKey) return null;
    if (phanCongMap.has(`${lopKey}-${monKey}`) || tkbMap.has(`${lopKey}-${monKey}`)) return monKey;
    const name = monNameById.get(monKey);
    if (!name) return null;
    const pc = phanCongByLopAndMonName.get(`${lopKey}-${name}`);
    if (pc) return normalizeId(pc.mon_id);
    const tkb = tkbByLopAndMonName.get(`${lopKey}-${name}`);
    if (tkb) return normalizeId(tkb.mon_id);
    return null;
  };

  const findPhanCongOrExisting = (lopId, monId) => {
    const pc = findPhanCong(lopId, monId);
    if (pc) return pc;
    const lopKey = normalizeId(lopId);
    const monKey = normalizeId(monId);
    const existing = tkbs.find(t => normalizeId(t.lop_id) === lopKey && normalizeId(t.mon_id) === monKey);
    if (!existing) return null;
    return {
      gv_id: existing.gv_id,
      mon_id: existing.mon_id,
      lop_id: existing.lop_id,
      ten_gv: existing.ten_gv,
      ten_mon: existing.ten_mon,
      ten_lop: existing.ten_lop
    };
  };

  const getMonById = (monId) => {
    const monKey = normalizeId(monId);
    return monHocs.find(m => normalizeId(m.mon_id) === monKey);
  };

  const decorateTkb = (tkb) => {
    if (!tkb) return tkb;
    const mon = monHocs.find(m => m.mon_id === tkb.mon_id);
    const gv = giaoViens.find(g => g.gv_id === tkb.gv_id);
    const lop = lopHocs.find(l => l.lop_id === tkb.lop_id);
    return {
      ...tkb,
      ten_mon: tkb.ten_mon || mon?.ten_mon,
      ten_gv: tkb.ten_gv || gv?.ten_gv,
      ten_lop: tkb.ten_lop || lop?.ten_lop
    };
  };

  const applyUpdatedTkbs = (updatedList) => {
    if (!updatedList?.length) return;
    const map = new Map(updatedList.map(t => [t.tkb_id, t]));
    setTkbs(prev => prev.map(t => (map.has(t.tkb_id) ? { ...t, ...map.get(t.tkb_id) } : t)));
  };

  const removeOneUnassigned = (gvId, monId, lopId) => {
    setUnassignedUnits(prev => {
      const idx = prev.findIndex(u => Number(u.gv_id) === Number(gvId) && Number(u.mon_id) === Number(monId) && Number(u.lop_id) === Number(lopId));
      if (idx < 0) return prev;
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
  };

  const buildUpdatePayload = (tkb, slot, skipOverlapCheck = true) => ({
    gvId: tkb.gv_id,
    monId: tkb.mon_id,
    lopId: tkb.lop_id,
    thu: slot.thu,
    tiet: slot.tiet,
    soTiet: Math.max(1, Number(tkb.so_tiet || 1)),
    buoi: slot.buoi,
    phong: tkb.phong || null,
    ghiChu: tkb.ghi_chu || null,
    skipOverlapCheck
  });

  const handleSwapRightClick = async (e, lopId, thu, buoi, tiet) => {
    if (e) e.preventDefault();
    if (!allowEdit || !swapEnabled) return;

    const currentKey = `${lopId}-${thu}-${buoi}-${tiet}`;

    if (!swapSource) {
      const sourceTkb = findTKB(lopId, thu, buoi, tiet);
      if (!sourceTkb) return;
      if (isFixed(lopId, thu, buoi, tiet)) {
        toast.warning('Ô này đang cố định, không thể đổi chỗ.');
        return;
      }
      setSwapSource({ lopId: String(lopId), thu, buoi, tiet, key: currentKey });
      return;
    }

    if (swapSource.key === currentKey) {
      setSwapSource(null);
      return;
    }

    if (String(swapSource.lopId) !== String(lopId)) {
      toast.warning('Chỉ đổi chỗ trong cùng một lớp.');
      setSwapSource(null);
      return;
    }

    if (isFixed(lopId, swapSource.thu, swapSource.buoi, swapSource.tiet) || isFixed(lopId, thu, buoi, tiet)) {
      toast.warning('Ô đang cố định, không thể đổi chỗ.');
      setSwapSource(null);
      return;
    }

    const sourceTkb = findTKB(lopId, swapSource.thu, swapSource.buoi, swapSource.tiet);
    const targetTkb = findTKB(lopId, thu, buoi, tiet);
    if (!sourceTkb || !targetTkb) {
      setSwapSource(null);
      return;
    }

    try {
      const sourceSlot = { thu: swapSource.thu, buoi: swapSource.buoi, tiet: swapSource.tiet };
      const targetSlot = { thu, buoi, tiet };

      // Hoán đổi NỘI DUNG (gv, mon, so_tiet) thay vì vị trí — không cần slot tạm,
      // không vi phạm unique constraint, 2 call chạy song song.
      const [moveSourceRes, moveTargetRes] = await Promise.all([
        tkbAPI.update(sourceTkb.tkb_id, {
          gvId: targetTkb.gv_id,
          monId: targetTkb.mon_id,
          lopId: sourceTkb.lop_id,
          thu: sourceSlot.thu,
          tiet: sourceSlot.tiet,
          soTiet: Math.max(1, Number(targetTkb.so_tiet || 1)),
          buoi: sourceSlot.buoi,
          phong: targetTkb.phong || null,
          ghiChu: targetTkb.ghi_chu || null,
          skipOverlapCheck: true,
        }),
        tkbAPI.update(targetTkb.tkb_id, {
          gvId: sourceTkb.gv_id,
          monId: sourceTkb.mon_id,
          lopId: targetTkb.lop_id,
          thu: targetSlot.thu,
          tiet: targetSlot.tiet,
          soTiet: Math.max(1, Number(sourceTkb.so_tiet || 1)),
          buoi: targetSlot.buoi,
          phong: sourceTkb.phong || null,
          ghiChu: sourceTkb.ghi_chu || null,
          skipOverlapCheck: true,
        }),
      ]);

      const updatedSource = decorateTkb(moveSourceRes.data?.data || moveSourceRes.data);
      const updatedTarget = decorateTkb(moveTargetRes.data?.data || moveTargetRes.data);
      applyUpdatedTkbs([updatedSource, updatedTarget]);

      const updatedMap = new Map([
        [updatedSource.tkb_id, updatedSource],
        [updatedTarget.tkb_id, updatedTarget]
      ]);
      const nextTkbs = tkbs.map(t => (updatedMap.has(t.tkb_id) ? { ...t, ...updatedMap.get(t.tkb_id) } : t));
      // Kiểm tra xung đột toàn bộ các tiết của cả 2 buổi bị ảnh hưởng
      const affectedSessions = [
        { thu: sourceSlot.thu, buoi: sourceSlot.buoi },
        { thu: targetSlot.thu, buoi: targetSlot.buoi },
      ].filter((s, i, arr) => arr.findIndex(x => x.thu === s.thu && x.buoi === s.buoi) === i);
      setSwapConflictHighlights(prev => {
        const next = { ...prev };
        for (const sess of affectedSessions) {
          for (let tiet = 1; tiet <= 5; tiet++) {
            const key = `${sess.thu}-${sess.buoi}-${tiet}`;
            const conflict = getConflictColorForSlot(nextTkbs, { thu: sess.thu, buoi: sess.buoi, tiet });
            if (conflict) next[key] = conflict; else delete next[key];
          }
        }
        return next;
      });

      setSwapSource(null);
      setHighlightGvId(targetTkb.gv_id);
    } catch (error) {
      console.error(error);
      fetchAll();
    }
  };

  // Tính điểm "gọn gàng" lịch của 1 GV: âm số tiết trống giữa các tiết trong mỗi buổi
  const computeGvScore = (gvId, tkbsList) => {
    const sessions = {};
    tkbsList.forEach(t => {
      if (String(t.gv_id) !== String(gvId) || t.tiet == null) return;
      const key = `${t.thu}-${t.buoi}`;
      if (!sessions[key]) sessions[key] = new Set();
      const soTiet = Math.max(1, Number(t.so_tiet || 1));
      for (let i = 0; i < soTiet; i++) sessions[key].add(Number(t.tiet) + i);
    });
    let totalGap = 0;
    Object.values(sessions).forEach(tietSet => {
      if (tietSet.size < 2) return;
      const tiets = [...tietSet];
      totalGap += (Math.max(...tiets) - Math.min(...tiets) + 1) - tiets.length;
    });
    return -totalGap;
  };

  // Rời ô: tìm vị trí tốt nhất ở buổi khác để đổi chỗ và thực hiện
  // eslint-disable-next-line no-unused-vars
  const handleReiO = async () => {
    if (!allowEdit) return;
    if (!selectedCell) { toast.warning('Chọn một ô TKB trước khi dùng "Rời ô".'); return; }
    const { lopId, thu, buoi, tiet } = selectedCell;

    const sourceTkb = findTKB(lopId, thu, buoi, tiet);
    if (!sourceTkb) { toast.warning('Ô này không có TKB để rời.'); return; }
    if (isFixed(lopId, thu, buoi, tiet)) { toast.warning('Ô đã cố định, không thể rời.'); return; }

    const srcGvId = String(sourceTkb.gv_id);
    const srcSoTiet = Math.max(1, Number(sourceTkb.so_tiet || 1));
    const srcScoreBefore = computeGvScore(srcGvId, tkbs);

    let bestImprovement = 0; // chỉ đổi nếu cải thiện được
    let bestTarget = null;

    for (const tgt of tkbs) {
      if (tgt.tkb_id === sourceTkb.tkb_id) continue;
      // Phải ở buổi khác
      if (Number(tgt.thu) === Number(thu) && tgt.buoi === buoi) continue;
      if (!tgt.gv_id || tgt.tiet == null) continue;
      if (isFixed(tgt.lop_id, tgt.thu, tgt.buoi, tgt.tiet)) continue;

      const tgtGvId = String(tgt.gv_id);
      const tgtSoTiet = Math.max(1, Number(tgt.so_tiet || 1));

      // Kiểm tra nội dung source (srcSoTiet) có vừa vào vị trí tgt không
      if (srcSoTiet !== tgtSoTiet) {
        let fits = true;
        for (let i = 0; i < srcSoTiet && fits; i++) {
          const p = Number(tgt.tiet) + i;
          if (tkbs.some(t =>
            t.tkb_id !== tgt.tkb_id && t.lop_id === tgt.lop_id &&
            Number(t.thu) === Number(tgt.thu) && t.buoi === tgt.buoi &&
            p >= Number(t.tiet) && p <= Number(t.tiet) + Math.max(1, Number(t.so_tiet || 1)) - 1
          )) fits = false;
        }
        for (let i = 0; i < tgtSoTiet && fits; i++) {
          const p = Number(sourceTkb.tiet) + i;
          if (tkbs.some(t =>
            t.tkb_id !== sourceTkb.tkb_id && t.lop_id === sourceTkb.lop_id &&
            Number(t.thu) === Number(sourceTkb.thu) && t.buoi === sourceTkb.buoi &&
            p >= Number(t.tiet) && p <= Number(t.tiet) + Math.max(1, Number(t.so_tiet || 1)) - 1
          )) fits = false;
        }
        if (!fits) continue;
      }

      // Kiểm tra GV nguồn không bị trùng tại vị trí đích
      let srcBusy = false;
      for (let i = 0; i < srcSoTiet && !srcBusy; i++) {
        const p = Number(tgt.tiet) + i;
        if (tkbs.some(t =>
          t.tkb_id !== tgt.tkb_id && String(t.gv_id) === srcGvId &&
          Number(t.thu) === Number(tgt.thu) && t.buoi === tgt.buoi &&
          p >= Number(t.tiet) && p <= Number(t.tiet) + Math.max(1, Number(t.so_tiet || 1)) - 1
        )) srcBusy = true;
      }
      if (srcBusy) continue;

      // Kiểm tra GV đích không bị trùng tại vị trí nguồn
      let tgtBusy = false;
      for (let i = 0; i < tgtSoTiet && !tgtBusy; i++) {
        const p = Number(sourceTkb.tiet) + i;
        if (tkbs.some(t =>
          t.tkb_id !== sourceTkb.tkb_id && String(t.gv_id) === tgtGvId &&
          Number(t.thu) === Number(sourceTkb.thu) && t.buoi === sourceTkb.buoi &&
          p >= Number(t.tiet) && p <= Number(t.tiet) + Math.max(1, Number(t.so_tiet || 1)) - 1
        )) tgtBusy = true;
      }
      if (tgtBusy) continue;

      // Mô phỏng đổi chỗ: hoán đổi gv_id
      const tgtScoreBefore = computeGvScore(tgtGvId, tkbs);
      const simTkbs = tkbs.map(t => {
        if (t.tkb_id === sourceTkb.tkb_id) return { ...t, gv_id: tgtGvId };
        if (t.tkb_id === tgt.tkb_id) return { ...t, gv_id: srcGvId };
        return t;
      });
      const improvement =
        (computeGvScore(srcGvId, simTkbs) - srcScoreBefore) +
        (computeGvScore(tgtGvId, simTkbs) - tgtScoreBefore);

      if (improvement > bestImprovement) {
        bestImprovement = improvement;
        bestTarget = tgt;
      }
    }

    if (!bestTarget) {
      toast.info('Không tìm được vị trí tốt hơn ở buổi khác để rời ô.');
      return;
    }

    try {
      const [r1, r2] = await Promise.all([
        tkbAPI.update(sourceTkb.tkb_id, {
          gvId: bestTarget.gv_id, monId: bestTarget.mon_id,
          lopId: sourceTkb.lop_id, thu: sourceTkb.thu, tiet: sourceTkb.tiet,
          soTiet: Math.max(1, Number(bestTarget.so_tiet || 1)),
          buoi: sourceTkb.buoi, phong: bestTarget.phong || null,
          ghiChu: bestTarget.ghi_chu || null, skipOverlapCheck: true,
        }),
        tkbAPI.update(bestTarget.tkb_id, {
          gvId: sourceTkb.gv_id, monId: sourceTkb.mon_id,
          lopId: bestTarget.lop_id, thu: bestTarget.thu, tiet: bestTarget.tiet,
          soTiet: Math.max(1, Number(sourceTkb.so_tiet || 1)),
          buoi: bestTarget.buoi, phong: sourceTkb.phong || null,
          ghiChu: sourceTkb.ghi_chu || null, skipOverlapCheck: true,
        }),
      ]);
      const u1 = decorateTkb(r1.data?.data || r1.data);
      const u2 = decorateTkb(r2.data?.data || r2.data);
      const updated = [u1, u2].filter(u => u?.tkb_id);
      applyUpdatedTkbs(updated);
      // Kiểm tra xung đột toàn bộ tiết của 2 buổi bị ảnh hưởng
      const updMap = new Map(updated.map(u => [u.tkb_id, u]));
      const nextTkbs = tkbs.map(t => updMap.has(t.tkb_id) ? { ...t, ...updMap.get(t.tkb_id) } : t);
      const affectedSessions = [
        { thu: sourceTkb.thu, buoi: sourceTkb.buoi },
        { thu: bestTarget.thu, buoi: bestTarget.buoi },
      ].filter((s, i, arr) => arr.findIndex(x => x.thu === s.thu && x.buoi === s.buoi) === i);
      setSwapConflictHighlights(prev => {
        const next = { ...prev };
        for (const sess of affectedSessions) {
          for (let tiet = 1; tiet <= 5; tiet++) {
            const key = `${sess.thu}-${sess.buoi}-${tiet}`;
            const conflict = getConflictColorForSlot(nextTkbs, { thu: sess.thu, buoi: sess.buoi, tiet });
            if (conflict) next[key] = conflict; else delete next[key];
          }
        }
        return next;
      });
    } catch (err) {
      toast.error('Có lỗi khi rời ô!');
    }
  };

  const handleSpreadSubjectAtCell = async (lopId, thu, buoi, tiet) => {
    if (!allowEdit || editMode !== 'spread') return;

    if (!selectedMonId) {
      toast.warning('Vui lòng chọn môn cần dải trước.');
      return;
    }

    if (!isBlocked(lopId, thu, buoi, tiet)) {
      return;
    }

    const resolvedMonId = resolveMonIdForLop(lopId, selectedMonId);
    if (!resolvedMonId) {
      toast.warning('Lớp này không học môn đã chọn.');
      return;
    }

    const pc = findPhanCongOrExisting(lopId, resolvedMonId);
    if (!pc) {
      toast.warning('Lớp này không học môn đã chọn.');
      return;
    }

    if (isFixed(lopId, thu, buoi, tiet)) {
      toast.warning('Ô này đang được cố định.');
      return;
    }

    const targetTkb = findTKB(lopId, thu, buoi, tiet);
    if (targetTkb && normalizeId(targetTkb.mon_id) === normalizeId(resolvedMonId) && normalizeId(targetTkb.gv_id) === normalizeId(pc.gv_id)) {
      return;
    }

    const relatedTkbs = tkbs.filter(t => normalizeId(t.lop_id) === normalizeId(lopId) && normalizeId(t.mon_id) === normalizeId(resolvedMonId) && normalizeId(t.gv_id) === normalizeId(pc.gv_id));
    const sourceTkb = relatedTkbs.find(t => !(t.thu === thu && t.buoi === buoi && t.tiet === tiet)) || null;
    const mon = getMonById(resolvedMonId);
    const totalUnits = Number(mon?.so_tiet_tuan || 0);
    const canCreate = !totalUnits || relatedTkbs.length < totalUnits;

    if (!sourceTkb && !canCreate) {
      toast.info('Không còn đơn vị TKB trống để xếp cho môn này.');
      return;
    }

    try {
      if (!sourceTkb && canCreate) {
        if (targetTkb) {
          toast.info('Ô này đã có TKB, không thể xếp mới.');
          return;
        }
        const createRes = await tkbAPI.create({
          gvId: pc.gv_id,
          monId: resolvedMonId,
          lopId,
          thu,
          tiet,
          soTiet: 1,
          buoi,
          phong: null,
          ghiChu: null
        });
        const newTkb = decorateTkb(createRes.data?.data || createRes.data);
        setTkbs(prev => [...prev, newTkb]);
        removeOneUnassigned(pc.gv_id, resolvedMonId, lopId);
     //   toast.success('Đã xếp đơn vị TKB.');
        return;
      }

      if (sourceTkb && isFixed(lopId, sourceTkb.thu, sourceTkb.buoi, sourceTkb.tiet)) {
        toast.warning('Đơn vị TKB đang được cố định nên không thể di chuyển.');
        return;
      }

      const sourceSlot = { thu: sourceTkb.thu, buoi: sourceTkb.buoi, tiet: sourceTkb.tiet };
      const targetSlot = { thu, buoi, tiet };

      if (!targetTkb) {
        const updateRes = await tkbAPI.update(sourceTkb.tkb_id, buildUpdatePayload(sourceTkb, targetSlot));
        applyUpdatedTkbs([decorateTkb(updateRes.data?.data || updateRes.data)]);
       // toast.success('Đã đổi vị trí đơn vị TKB.');
        return;
      }

      const tempSlot = { thu: 0, buoi: 'Tam', tiet: 1 };
      await tkbAPI.update(targetTkb.tkb_id, buildUpdatePayload(targetTkb, tempSlot));
      const moveSourceRes = await tkbAPI.update(sourceTkb.tkb_id, buildUpdatePayload(sourceTkb, targetSlot));
      const moveTargetRes = await tkbAPI.update(targetTkb.tkb_id, buildUpdatePayload(targetTkb, sourceSlot));

      applyUpdatedTkbs([
        decorateTkb(moveSourceRes.data?.data || moveSourceRes.data),
        decorateTkb(moveTargetRes.data?.data || moveTargetRes.data)
      ]);
     // toast.success('Đã đổi chỗ đơn vị TKB.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi dải môn!');
      console.error(error);
      fetchAll();
    }
  };

  const rows = useMemo(() => {
    const builtRows = [];

    for (const thu of thuList) {
      for (const buoi of buoiList) {
        for (const tiet of tietList) {
          builtRows.push({
            key: `${thu.value}-${buoi.value}-${tiet}`,
            thu: thu.value,
            buoi: buoi.value,
            tiet,
            thuLabel: thu.label,
            buoiLabel: buoi.label,
            isTemp: false
          });
        }
      }
    }

    const hasOverflowTKB = tkbs.some(t =>
      t.thu < 2 || t.thu > 7 || !['Sang', 'Chieu'].includes(t.buoi) || t.tiet < 1 || t.tiet > 5
    );

    if (unassignedUnits.length > 0 || hasOverflowTKB) {
      for (let tiet = 1; tiet <= 20; tiet++) {
        builtRows.push({
          key: `temp-${tiet}`,
          thu: 0,
          buoi: 'Tam',
          tiet,
          thuLabel: 'Tạm',
          buoiLabel: 'Tạm',
          isTemp: true
        });
      }
    }

    return builtRows;
  }, [tkbs, unassignedUnits, thuList, buoiList, tietList]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Đang tải...</p>
      </div>
    );
  }

  return (
    <div className="tkb-page">
      <div className="tkb-container">
        {/* Bảng TKB chính */}
        <div className="tkb-main-panel">
          <div className="panel-header">
            {/* Hidden file input for import blocked */}
            <input
              ref={importBlockedRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleImportFileChange}
            />

            {/* Top row */}
            <div className="toolbar-top-row">
              {/* View mode: segmented control */}
              <div className="tkb-segment-group">
                {[
                  { value: 'normal',      icon: '🎨', label: 'Màu sắc' },
                  { value: 'dim',         icon: <FiEye />, label: 'Nổi bật' },
                  { value: 'showBlocked', icon: <FiSlash />, label: 'Vùng học' },
                ].map(({ value, icon, label }) => (
                  <button
                    key={value}
                    className={`tkb-segment${editMode !== 'block' && viewMode === value ? ' active' : ''}`}
                    onClick={() => {
                      if (editMode === 'block') { prevViewModeRef.current = value; setEditMode('view'); setActiveToolTab(null); }
                      setViewMode(value); setEditMode('view');
                    }}
                  >
                    <span className="tkb-segment-icon">{icon}</span>
                    <span>{label}</span>
                  </button>
                ))}
              </div>

              <div className="tkb-toolbar-divider" />

              {/* Tạo TKB tự động — hero button */}
              <button
                className="tkb-action-btn tkb-hero-btn"
                style={{ opacity: isAutoScheduling ? 0.65 : 1 }}
                onClick={() => { handleSpreadUnits(); setActiveToolTab(null); }}
                disabled={isAutoScheduling}
              >
                {isAutoScheduling
                  ? <span className="tkb-hero-spinner" />
                  : <span className="tkb-hero-icon">✦</span>
                }
                <div className="tkb-hero-text">
                  <span className="tkb-hero-main">Tạo thời khóa biểu</span>
                  <span className="tkb-hero-sub">tự động</span>
                </div>
              </button>

              {/* Tạo vùng xếp — nhỏ gọn, ngay bên phải */}
              <button
                className="tkb-action-btn"
                style={{
                  background: 'rgba(255,255,255,0.10)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: 8,
                  padding: '4px 9px',
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.80)',
                  whiteSpace: 'nowrap',
                }}
                onClick={handleAutoGenerateVungXep}
                title="Tạo vùng xếp TKB tự động"
              >
                <FiZap size={12} />
                <span>Tạo vùng xếp</span>
              </button>

              {/* Spacer */}
              <div style={{ flex: 1 }} />

              {/* Nhóm Xếp thủ công */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 2,
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 8, padding: '2px 6px',
                background: 'rgba(255,255,255,0.07)',
              }}>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', paddingRight: 4, borderRight: '1px solid rgba(255,255,255,0.15)', marginRight: 2, whiteSpace: 'nowrap' }}>Xếp thủ công</span>

                {/* Bôi đánh dấu */}
                <div className="boi-dropdown">
                  <button
                    className={`tkb-action-btn boi-btn${activeToolTab === 'co-dinh' ? ' active' : ''}`}
                    style={{ padding: '3px 8px', fontSize: 12 }}
                    onClick={() => handleTabChange('co-dinh')}
                  >
                    <FiEdit size={13} className="tkb-btn-icon" />
                    <span className="tkb-btn-label">Bôi</span>
                    <FiChevronDown size={11} className={`boi-chevron${activeToolTab === 'co-dinh' ? ' open' : ''}`} />
                  </button>

                  {activeToolTab === 'co-dinh' && (
                    <div className="boi-dropdown-panel">
                      {/* Radio 1: Vùng xếp */}
                      <div
                        className={`boi-radio-item${editMode === 'block' ? ' active' : ''}`}
                        onClick={() => {
                          if (editMode === 'block') { setEditMode('view'); setViewMode(prevViewModeRef.current); }
                          else { prevViewModeRef.current = viewMode; setViewMode('showBlocked'); setEditMode('block'); setSwapEnabled(false); setSwapSource(null); }
                        }}
                      >
                        <div className="boi-radio-dot" />
                        <FiSlash className="boi-radio-icon" />
                        <div className="boi-radio-text">
                          <span className="boi-radio-label">Vùng xếp</span>
                          <span className="boi-radio-desc">Đánh dấu ô sẽ học trong tuần</span>
                        </div>
                      </div>

                      {/* Radio 2: Bôi môn */}
                      <div
                        className={`boi-radio-item${editMode === 'spread' ? ' active' : ''}`}
                        onClick={() => {
                          if (editMode === 'spread') { setEditMode('view'); return; }
                          if (!selectedMonId) { toast.warning('Vui lòng chọn môn cần dải trước.'); return; }
                          setViewMode('normal');
                          setEditMode('spread'); setSwapEnabled(false); setSwapSource(null);
                        }}
                      >
                        <div className="boi-radio-dot" />
                        <FiGrid className="boi-radio-icon" />
                        <div className="boi-radio-text" style={{ flex: 1 }}>
                          <span className="boi-radio-label">Bôi môn</span>
                          <select
                            className="boi-mon-select"
                            value={selectedMonId}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              const id = e.target.value;
                              setSelectedMonId(id);
                              if (id) { setViewMode('normal'); setEditMode('spread'); setSwapEnabled(false); setSwapSource(null); }
                              else { setEditMode('view'); }
                            }}
                          >
                            <option value="">-- Chọn môn để bôi --</option>
                            {sortedMonHocs.map(mon => (
                              <option key={mon.mon_id} value={mon.mon_id}>{mon.ten_mon}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Radio 3: Cố định môn học */}
                      <div
                        className={`boi-radio-item${editMode === 'fixed' ? ' active' : ''}`}
                        onClick={() => {
                          if (editMode === 'fixed') { setEditMode('view'); return; }
                          setViewMode('normal');
                          setEditMode('fixed'); setFixedAction('toggle'); setSwapEnabled(false); setSwapSource(null);
                        }}
                      >
                        <div className="boi-radio-dot" />
                        <FiLock className="boi-radio-icon" />
                        <div className="boi-radio-text" style={{ flex: 1 }}>
                          <span className="boi-radio-label">Cố định môn học</span>
                          <span className="boi-radio-desc">Click/kéo lên ô TKB để cố định</span>
                          {editMode === 'fixed' && (
                            <button
                              className="boi-sub-btn danger"
                              style={{ marginTop: 6, alignSelf: 'flex-start' }}
                              onClick={(e) => { e.stopPropagation(); handleClearAllFixed(); }}
                            >
                              <FiUnlock /> Bỏ cố định các ô
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Xếp TKB buổi đang chọn — compact */}
                <button
                  className="tkb-action-btn schedule-btn"
                  style={{ padding: '3px 8px', fontSize: 12, whiteSpace: 'nowrap' }}
                  onClick={handleScheduleSession}
                  disabled={isAutoScheduling}
                  title={activeSession.thu ? `Xếp ${activeSession.buoi === 'Sang' ? 'Sáng' : 'Chiều'} Thứ ${activeSession.thu}` : 'Click vào 1 ô trong bảng để chọn buổi'}
                >
                  <FiClock size={13} className="tkb-btn-icon" />
                  <span className="tkb-btn-label">
                    {activeSession.thu
                      ? `Xếp buổi ${activeSession.buoi === 'Sang' ? '☀️' : '🌙'} T${activeSession.thu}`
                      : 'Xếp buổi'}
                  </span>
                </button>

                {/* Đổi chỗ — compact */}
                <button
                  className={`tkb-action-btn tkb-edit-toggle-btn${swapEnabled ? ' active' : ''}`}
                  style={{ padding: '3px 8px', fontSize: 12, whiteSpace: 'nowrap' }}
                  onClick={() => { setSwapEnabled(v => !v); if (swapEnabled) setSwapSource(null); }}
                  title={swapEnabled ? 'Tắt đổi chỗ thủ công' : 'Bật đổi chỗ thủ công'}
                >
                  <FiEdit size={13} className="tkb-btn-icon" />
                  <span className="tkb-btn-label">{swapEnabled ? 'Đổi chỗ ✓' : 'Đổi chỗ'}</span>
                </button>
              </div>

              <div className="tkb-toolbar-divider" />

              {/* Xóa TKB — thật nhỏ để tránh click nhầm */}
              <button
                className="tkb-action-btn delete-btn"
                style={{
                  padding: '3px 6px',
                  fontSize: 11,
                  opacity: (isAutoScheduling || tkbs.length === 0) ? 0.4 : 0.65,
                  minWidth: 'unset',
                }}
                onClick={() => { setConfirmDeleteAll(true); setActiveToolTab(null); }}
                disabled={isAutoScheduling || tkbs.length === 0}
                title="Xóa toàn bộ TKB"
              >
                <FiTrash2 size={12} />
              </button>

              <div className="tkb-toolbar-divider" />

              {/* Zoom — far right */}
              <div className="tkb-zoom-group">
                <button className="tkb-zoom-btn" onClick={() => setZoomLevel(z => { const n = Math.max(50, z - 10); localStorage.setItem('tkb_zoom', n); return n; })} disabled={zoomLevel <= 50}>−</button>
                <span className="tkb-zoom-pct">{zoomLevel}%</span>
                <button className="tkb-zoom-btn" onClick={() => setZoomLevel(z => { const n = Math.min(200, z + 10); localStorage.setItem('tkb_zoom', n); return n; })} disabled={zoomLevel >= 200}>+</button>
              </div>
            </div>


            {/* Status bar: hiển thị trạng thái active */}
            {editMode !== 'view' && (
              <div className="toolbar-status-bar">
                {editMode === 'block' && <span className="status-badge block">Bôi vùng xếp — số ô bôi không vượt tổng tiết/tuần của lớp (theo phân công). Click/kéo ô trống</span>}
                {editMode === 'spread' && <span className="status-badge spread">Dải môn: {sortedMonHocs.find(m => String(m.mon_id) === String(selectedMonId))?.ten_mon || ''} - Click/Kéo lên ô đánh dấu</span>}
                {editMode === 'fixed' && fixedAction === 'fix' && <span className="status-badge fixed">Bôi cố định - Click/Kéo lên ô có TKB</span>}
                {editMode === 'fixed' && fixedAction === 'unfix' && <span className="status-badge fixed-off">Bôi bỏ cố định - Click/Kéo lên ô cố định</span>}
              </div>
            )}

          </div>

          {lopHocs.length === 0 ? (
              <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                <p>Chưa có lớp học nào. Vui lòng thêm lớp học trước.</p>
              </div>
            ) : (
              <div 
                className="tkb-table-wrapper"
                style={{ 
                  zoom: `${zoomLevel}%`
                }}
              >
                <table className={`tkb-table ${viewMode === 'dim' && highlightGvId ? 'has-highlight' : ''}`}>
                  <thead>
                    <tr>
                      <th className="sticky-col header-cell">Thứ</th>
                      <th className="sticky-col-2 header-cell">Buổi</th>
                      <th className="sticky-col-3 header-cell">Tiết</th>
                      {lopHocs.map(lop => (
                        <th key={lop.lop_id} className="lop-header">
                          {lop.ten_lop}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => {
                    // Xác định có hiển thị cell Thứ và Buổi không
                    let showThu, showBuoi;
                    
                    if (row.isTemp) {
                      // Dòng tạm thời
                      showThu = row.tiet === 1;
                      showBuoi = row.tiet === 1;
                    } else {
                      // Dòng bình thường
                      showThu = row.tiet === 1 && row.buoi === 'Sang';
                      showBuoi = row.tiet === 1;
                    }
                    
                    return (
                      <tr key={row.key} className={`
                        ${row.buoi === 'Chieu' && row.tiet === 1 ? 'buoi-chieu-row' : ''} 
                        ${showThu ? 'thu-separator' : ''} 
                        ${row.isTemp ? 'temp-row' : ''}
                        ${row.isTemp && row.tiet === 1 ? 'temp-separator' : ''}
                      `}>
                        {showThu && (() => {
                          const thuConflictInfo = !row.isTemp ? getConflictColorForThu(row.thu) : null;
                          const thuConflictColor = thuConflictInfo?.color || null;
                          const conflictText = thuConflictColor ? getContrastTextColor(thuConflictColor) : null;
                          return (
                            <td
                              className={`sticky-col ${row.isTemp ? 'temp-thu-cell' : 'thu-cell'} ${thuConflictColor ? 'conflict-slot' : ''}`}
                              rowSpan={row.isTemp ? 20 : 10}
                              style={
                                thuConflictColor
                                  ? { '--conflict-bg': thuConflictColor, '--conflict-text': conflictText }
                                  : undefined
                              }
                              onClick={() => {
                                if (thuConflictInfo?.gvId) {
                                  toggleHighlightTeacher(thuConflictInfo.gvId);
                                }
                              }}
                            >
                              {row.thuLabel}
                            </td>
                          );
                        })()}
                        {showBuoi && !row.isTemp && (
                          <td className={`sticky-col-2 buoi-cell ${row.buoi === 'Sang' ? 'buoi-sang' : 'buoi-chieu'}`} rowSpan={5}>
                            {row.buoiLabel}
                          </td>
                        )}
                        {showBuoi && row.isTemp && (
                          <td className="sticky-col-2 temp-buoi-cell" rowSpan={20}>
                            {row.buoiLabel}
                          </td>
                        )}
                        {(() => {
                          const conflictKey = `${row.thu}-${row.buoi}-${row.tiet}`;
                          const conflictInfo = !row.isTemp ? swapConflictHighlights[conflictKey] : null;
                          const conflictColor = conflictInfo?.color || null;
                          const conflictText = conflictColor ? getContrastTextColor(conflictColor) : null;
                          return (
                            <td
                              className={`sticky-col-3 ${row.isTemp ? 'temp-tiet-cell' : 'tiet-cell'} ${conflictColor ? 'conflict-slot' : ''}`}
                              style={
                                conflictColor
                                  ? { '--conflict-bg': conflictColor, '--conflict-text': conflictText }
                                  : undefined
                              }
                              onClick={() => {
                                if (conflictInfo?.gvId) {
                                  toggleHighlightTeacher(conflictInfo.gvId);
                                }
                              }}
                            >
                              {row.tiet}
                            </td>
                          );
                        })()}
                        {lopHocs.map(lop => {
                          // Đối với dòng tạm thời, hiển thị TKB dư thừa và các đơn vị chưa xếp được
                          if (row.isTemp) {
                            if (row.tiet === 1 && lop.lop_id === lopHocs[0]?.lop_id) {
                              console.log('Current unassignedUnits:', unassignedUnits); // Debug log at first temp row
                            }
                            // 1. Tìm TKB dư thừa cho lớp này (TKB đã lưu nhưng nằm ngoài vùng hợp lệ)
                            const overflowTKBs = tkbs.filter(t => 
                              t.lop_id === lop.lop_id && 
                              (t.thu < 2 || t.thu > 7 || !['Sang', 'Chieu'].includes(t.buoi) || t.tiet < 1 || t.tiet > 5)
                            );
                            
                            // 2. Tìm các đơn vị chưa xếp được cho lớp này
                            const unassignedForLop = unassignedUnits.filter(u => u.lop_id === lop.lop_id);
                            console.log(`Row ${row.tiet}: unassigned for lop ${lop.lop_id}:`, unassignedForLop); // Debug log
                            
                            // 3. Kết hợp cả hai loại: overflow TKB + unassigned units
                            const allTempUnits = [
                              ...overflowTKBs,
                              ...unassignedForLop.map(u => ({
                                ten_mon: u.ten_mon,
                                ten_gv: u.ten_gv,
                                gv_id: u.gv_id,
                                reason: u.reason,
                                isUnassigned: true
                              }))
                            ];
                            console.log(`Row ${row.tiet}: allTempUnits for lop ${lop.lop_id}:`, allTempUnits); // Debug log
                            
                            const tempUnit = allTempUnits[row.tiet - 1]; // Lấy unit theo chỉ số
                            
                            const tempGvColor = tempUnit?.gv_id ? getGvColor(tempUnit.gv_id) : null;
                            const isTempHighlighted = tempUnit?.gv_id && highlightGvId && String(tempUnit.gv_id) === String(highlightGvId);
                            return (
                              <td
                                key={`${row.key}-${lop.lop_id}`}
                                className={`tkb-cell temp-cell ${tempUnit?.isUnassigned ? 'unassigned-unit' : ''} ${tempGvColor ? 'has-gv' : ''} ${isTempHighlighted ? 'gv-highlight' : ''}`}
                                style={tempGvColor ? { backgroundColor: tempGvColor, color: getContrastTextColor(tempGvColor) } : undefined}
                                onClick={() => tempUnit?.gv_id && toggleHighlightTeacher(tempUnit.gv_id)}
                                title={
                                  tempUnit
                                    ? tempUnit.isUnassigned
                                      ? `${tempUnit.ten_mon} - ${tempUnit.ten_gv} (${
                                          tempUnit.reason === 'no_session' ? 'Không tìm được buổi phù hợp' :
                                          tempUnit.reason === 'no_slot' ? 'Không có tiết trống trong buổi' :
                                          tempUnit.reason === 'conflict' ? 'Trùng lịch GV/lớp' :
                                          'Không xếp được'
                                        })`
                                      : `${tempUnit.ten_mon} - ${tempUnit.ten_gv} (Dư thừa)`
                                    : 'Ô dự phòng'
                                }
                              >
                                {tempUnit ? (
                                  <div className={`tkb-content temp-content ${tempUnit.isUnassigned ? 'unassigned' : ''}`}>
                                    <span className="mon-name">{getMonShortName(tempUnit.ten_mon)}</span>
                                    {tempUnit.isUnassigned && <span className="unassigned-marker">!</span>}
                                  </div>
                                ) : null}
                              </td>
                            );
                          }
                          
                          // Logic cũ cho các dòng bình thường
                          const tkb = findTKB(lop.lop_id, row.thu, row.buoi, row.tiet);
                          const blocked = isBlocked(lop.lop_id, row.thu, row.buoi, row.tiet);
                          const fixed = isFixed(lop.lop_id, row.thu, row.buoi, row.tiet);
                          const isHighlighted = tkb && highlightGvId && String(tkb.gv_id) === String(highlightGvId);
                          const resolvedMonId = selectedMonId ? resolveMonIdForLop(lop.lop_id, selectedMonId) : null;
                          const hasPhanCong = Boolean(resolvedMonId);
                          const pc = hasPhanCong ? findPhanCongOrExisting(lop.lop_id, resolvedMonId) : null;
                          const alreadyPlaced = tkb && resolvedMonId && pc &&
                            normalizeId(tkb.mon_id) === normalizeId(resolvedMonId) &&
                            normalizeId(tkb.gv_id) === normalizeId(pc.gv_id);
                          const hasOtherTkb = Boolean(tkb && !alreadyPlaced);
                          const canSpread = editMode === 'spread' && resolvedMonId && blocked && hasPhanCong && !fixed && !hasOtherTkb;
                          const isSwapSource = swapSource && swapSource.key === `${lop.lop_id}-${row.thu}-${row.buoi}-${row.tiet}`;
                          
                          let cellClass = 'tkb-cell';
                          if ((editMode === 'block' || viewMode === 'showBlocked') && blocked) cellClass += ' will-study';
                          else if (tkb) {
                            cellClass += ' has-tkb has-gv';
                            if (fixed) cellClass += ' fixed';
                          }
                          else if (editMode === 'block') cellClass += ' block-mode';
                          else if (editMode === 'fixed') cellClass += ' fixed-mode';
                          else if (editMode === 'spread') cellClass += ' spread-mode';
                          if (editMode === 'spread') cellClass += canSpread ? ' spread-eligible' : ' spread-disabled';
                          if (viewMode === 'showFixed' && fixed) cellClass += ' fixed-strong';
                          if (isSwapSource) cellClass += ' swap-source';
                          if (isHighlighted) cellClass += ' gv-highlight';
                          
                          return (
                            <td
                              key={`${row.key}-${lop.lop_id}`}
                              className={cellClass}
                              style={
                                tkb
                                  ? (() => {
                                      const bg = getGvColor(tkb.gv_id);
                                      return { backgroundColor: bg, color: getContrastTextColor(bg) };
                                    })()
                                  : undefined
                              }
                              onClick={(e) => {
                                if (e.button !== 0) return; // bỏ qua right-click
                                if (isDragging) return;
                                if (tkb) {
                                  toggleHighlightTeacher(tkb.gv_id);
                                  setSelectedCell({ lopId: lop.lop_id, thu: row.thu, buoi: row.buoi, tiet: row.tiet });
                                } else {
                                  setHighlightGvId(null);
                                  setSelectedCell(null);
                                }
                                handleCellClick(lop.lop_id, row.thu, row.buoi, row.tiet);
                              }}
                              onMouseDown={() => handleMouseDown(lop.lop_id, row.thu, row.buoi, row.tiet)}
                              onMouseEnter={() => handleMouseEnter(lop.lop_id, row.thu, row.buoi, row.tiet)}
                              onMouseUp={handleMouseUp}
                              onContextMenu={(e) => handleSwapRightClick(e, lop.lop_id, row.thu, row.buoi, row.tiet)}
                              title={
                                editMode === 'spread' && !selectedMonId ? 'Chọn môn cần dải trước' :
                                editMode === 'spread' && !blocked ? 'Ô chưa được đánh dấu sẽ học' :
                                editMode === 'spread' && !hasPhanCong ? 'Lớp này không học môn đã chọn' :
                                editMode === 'spread' && fixed ? 'Ô đang cố định' :
                                editMode === 'spread' && hasOtherTkb ? 'Ô đã có TKB môn khác' :
                                editMode === 'spread' ? 'Bôi để dải môn' :
                                editMode === 'block' && blocked ? 'Sẽ học (kéo để bỏ đánh dấu)' :
                                editMode === 'block' ? 'Kéo chuột để đánh dấu sẽ học' :
                                fixed && tkb ? `🔒 ${tkb.ten_mon} - ${tkb.ten_gv} (Đã cố định)` :
                                fixed ? '🔒 (Đã cố định)' :
                                tkb ? `${tkb.ten_mon} - ${tkb.ten_gv}` :
                                editMode === 'fixed' ? 'Chọn ô có TKB để cố định' :
                                ''
                              }
                            >
                              {editMode === 'block' && blocked ? (
                                <span className="marked-study" style={{ color: '#4caf50' }}>✓</span>
                              ) : tkb ? (
                                <div className={`tkb-content ${fixed ? 'fixed' : ''}`}>
                                  <span className="mon-name">{getMonShortName(tkb.ten_mon)}</span>
                                  {fixed && <FiLock className="fixed-icon" />}
                                </div>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      </div>

      {/* Confirm xóa toàn bộ TKB */}
      <ConfirmDialog
        isOpen={confirmDeleteAll}
        onClose={() => setConfirmDeleteAll(false)}
        onConfirm={handleDeleteAllTKB}
        title="Xóa toàn bộ TKB"
        message={`Bạn có chắc chắn muốn xóa toàn bộ ${tkbs.length} tiết học? Hành động này không thể hoàn tác!`}
      />

      {/* Confirm xóa vùng xếp */}
      <ConfirmDialog
        isOpen={confirmClearBlocked}
        onClose={() => setConfirmClearBlocked(false)}
        onConfirm={() => { setConfirmClearBlocked(false); handleClearAllBlocked(); }}
        title="Xóa vùng xếp"
        message={`Bạn có chắc chắn muốn xóa toàn bộ đánh dấu vùng xếp? Hành động này không thể hoàn tác!`}
      />

      {/* Dialog sao đánh dấu từ lớp khác */}
      {copyBlockedDialogOpen && (
        <div className="modal-overlay" onClick={() => {
          setCopyBlockedDialogOpen(false);
          setSelectedTargetLops([]);
        }}>
          <div className="schedule-session-dialog" onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Sao đánh dấu từ lớp khác</h3>
              <button className="close-btn" onClick={() => {
                setCopyBlockedDialogOpen(false);
                setSelectedTargetLops([]);
              }}>
                <FiX />
              </button>
            </div>
            <div className="dialog-body">
              <p className="dialog-desc">
                Sao chép các ô đánh dấu "Sẽ học" từ lớp nguồn sang nhiều lớp đích cùng lúc.
              </p>
              <div className="form-group">
                <label>Lớp nguồn:</label>
                <select 
                  value={sourceLopForCopy || ''} 
                  onChange={e => setSourceLopForCopy(e.target.value)}
                  className="form-control"
                >
                  <option value="">-- Chọn lớp nguồn --</option>
                  {lopHocs.map(lop => (
                    <option key={lop.lop_id} value={lop.lop_id}>
                      {lop.ten_lop}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Các lớp đích:</label>
                <div style={{ marginBottom: '8px' }}>
                  <button 
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => {
                      const availableLops = lopHocs
                        .filter(lop => lop.lop_id !== sourceLopForCopy)
                        .map(lop => lop.lop_id);
                      setSelectedTargetLops(availableLops);
                    }}
                    style={{ marginRight: '8px' }}
                    disabled={!sourceLopForCopy}
                  >
                    Chọn tất cả
                  </button>
                  <button 
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSelectedTargetLops([])}
                  >
                    Bỏ chọn tất cả
                  </button>
                </div>
                <div className="target-lops-list" style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  padding: '8px'
                }}>
                  {lopHocs
                    .filter(lop => lop.lop_id !== sourceLopForCopy)
                    .map(lop => (
                      <label key={lop.lop_id} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        marginBottom: '6px',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedTargetLops.includes(lop.lop_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTargetLops(prev => [...prev, lop.lop_id]);
                            } else {
                              setSelectedTargetLops(prev => prev.filter(id => id !== lop.lop_id));
                            }
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        <span>{lop.ten_lop}</span>
                      </label>
                    ))}
                </div>
                {lopHocs.filter(lop => lop.lop_id !== sourceLopForCopy).length === 0 && (
                  <p style={{ color: '#666', fontStyle: 'italic', marginTop: '8px' }}>
                    Không có lớp nào khác để sao chép
                  </p>
                )}
              </div>
            </div>
            <div className="dialog-footer">
              <button className="btn btn-secondary" onClick={() => {
                setCopyBlockedDialogOpen(false);
                setSelectedTargetLops([]);
              }}>
                Hủy
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleCopyBlockedFromClass}
                disabled={!sourceLopForCopy || selectedTargetLops.length === 0}
              >
                <FiLayers /> Sao chép ({selectedTargetLops.length} lớp)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog chuyển đơn vị TKB của GV quá tải sang buổi khác */}
      {overloadMoveDialog.open && (
        <div className="modal-overlay" onClick={() => setOverloadMoveDialog(prev => ({ ...prev, open: false }))}>
          <div className="schedule-session-dialog overload-move-dialog" onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Chuyển tiết GV quá tải</h3>
              <button className="close-btn" onClick={() => setOverloadMoveDialog(prev => ({ ...prev, open: false }))}>
                <FiX />
              </button>
            </div>
            <div className="dialog-body">
              <div className="overload-gv-info">
                <span className="overload-gv-name">{overloadMoveDialog.gvName}</span>
                <span className="overload-gv-badge">
                  {overloadMoveDialog.soTiet}/{overloadMoveDialog.maxTiet} tiết
                </span>
                <span className="overload-gv-note">— vượt {overloadMoveDialog.soTiet - overloadMoveDialog.maxTiet} tiết</span>
              </div>

              <div className="overload-two-col">
                <div className="overload-col">
                  <div className="overload-col-label">Chọn đơn vị cần chuyển:</div>
                  <div className="overload-unit-list">
                    {overloadMoveDialog.units.map(unit => {
                      const isSelected = overloadMoveDialog.selectedUnit?.tkb_id === unit.tkb_id;
                      return (
                        <div
                          key={unit.tkb_id}
                          className={`overload-unit-item${isSelected ? ' selected' : ''}`}
                          onClick={() => {
                            const slots = findSuggestedSlotsForUnit(unit, overloadMoveDialog.sourceThu, overloadMoveDialog.sourceBuoi);
                            setOverloadMoveDialog(prev => ({
                              ...prev,
                              selectedUnit: unit,
                              suggestedSlots: slots,
                              selectedSlot: null,
                            }));
                          }}
                        >
                          <span className="unit-mon">{unit.ten_mon}</span>
                          <span className="unit-lop">{unit.ten_lop}</span>
                          <span className="unit-tiet">T{unit.tiet}{unit.so_tiet > 1 ? `–${Number(unit.tiet) + Number(unit.so_tiet) - 1}` : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="overload-col">
                  <div className="overload-col-label">Chọn vị trí đích:</div>
                  {overloadMoveDialog.selectedUnit ? (
                    overloadMoveDialog.suggestedSlots.length > 0 ? (
                      <div className="overload-slot-list">
                        {overloadMoveDialog.suggestedSlots.map((slot, idx) => {
                          const isSelected =
                            overloadMoveDialog.selectedSlot?.thu === slot.thu &&
                            overloadMoveDialog.selectedSlot?.buoi === slot.buoi &&
                            overloadMoveDialog.selectedSlot?.tiet === slot.tiet;
                          return (
                            <div
                              key={idx}
                              className={`overload-slot-item${isSelected ? ' selected' : ''}`}
                              onClick={() => setOverloadMoveDialog(prev => ({ ...prev, selectedSlot: slot }))}
                            >
                              <span className="slot-thu">{slot.thuLabel}</span>
                              <span className="slot-buoi">{slot.buoiLabel}</span>
                              <span className="slot-tiet">Tiết {slot.tiet}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="overload-no-slots">Không tìm thấy vị trí phù hợp</div>
                    )
                  ) : (
                    <div className="overload-no-slots">← Chọn đơn vị trước</div>
                  )}
                </div>
              </div>
            </div>
            <div className="dialog-footer">
              <button className="btn btn-secondary" onClick={() => setOverloadMoveDialog(prev => ({ ...prev, open: false }))}>
                Hủy
              </button>
              <button
                className="btn btn-primary"
                onClick={handleOverloadMove}
                disabled={!overloadMoveDialog.selectedUnit || !overloadMoveDialog.selectedSlot}
              >
                Chuyển tiết
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog gom buổi giáo viên */}
      {compactDialog.open && (() => {
        const thuLabel = (thu) => ['', '', 'Hai', 'Ba', 'Tư', 'Năm', 'Sáu', 'Bảy'][thu] || `T${thu}`;
        const buoiLabel = (b) => b === 'Sang' ? 'Sáng' : 'Chiều';
        const { candidates, selected, executing } = compactDialog;

        // Thống kê tổng buổi được giảm khi thực hiện các đổi chỗ đang được chọn
        const toExec = candidates.filter(c => selected.has(c.id));
        const totalSessSaved = toExec.reduce(
          (acc, c) => acc + Math.max(0, -c.deltaSessA) + Math.max(0, -c.deltaSessB), 0
        );
        const totalSessAdded = toExec.reduce(
          (acc, c) => acc + Math.max(0, c.deltaSessA) + Math.max(0, c.deltaSessB), 0
        );

        // Đếm theo category
        const counts = { safe: 0, gap: 0, risky: 0 };
        candidates.forEach(c => counts[c.category]++);

        return (
          <div className="modal-overlay" onClick={() => !executing && setCompactDialog(prev => ({ ...prev, open: false }))}>
            <div className="schedule-session-dialog compact-dialog" onClick={e => e.stopPropagation()} style={{ maxWidth: 720, width: '95vw' }}>
              <div className="dialog-header">
                <h3>Gợi ý gom buổi dạy giáo viên</h3>
                <button className="close-btn" onClick={() => setCompactDialog(prev => ({ ...prev, open: false }))} disabled={executing}>
                  <FiX />
                </button>
              </div>

              {/* Thanh tóm tắt kết quả dự kiến */}
              <div className="compact-summary-bar">
                <span className="compact-summary-item">
                  <strong>{selected.size}</strong> / {candidates.length} đổi chỗ chọn
                </span>
                {totalSessSaved > 0 && (
                  <span className="compact-summary-item good">
                    −{totalSessSaved} buổi giảm
                  </span>
                )}
                {totalSessAdded > 0 && (
                  <span className="compact-summary-item bad">
                    +{totalSessAdded} buổi tăng
                  </span>
                )}
                <span className="compact-summary-net" style={{ color: totalSessSaved - totalSessAdded > 0 ? '#16a34a' : '#6b7280' }}>
                  Ròng: {totalSessSaved - totalSessAdded > 0 ? `−${totalSessSaved - totalSessAdded}` : totalSessSaved - totalSessAdded < 0 ? `+${totalSessAdded - totalSessSaved}` : '0'} buổi
                </span>
              </div>

              {/* Bộ lọc nhanh */}
              <div className="compact-filter-bar">
                <span style={{ fontSize: 12, color: '#6b7280', marginRight: 4 }}>Chọn nhanh:</span>
                {counts.safe > 0 && (
                  <button
                    className="compact-filter-btn safe"
                    onClick={() => setCompactDialog(prev => ({
                      ...prev,
                      selected: new Set(prev.candidates.filter(c => c.category === 'safe').map(c => c.id))
                    }))}
                  >
                    An toàn ({counts.safe})
                  </button>
                )}
                {counts.gap > 0 && (
                  <button
                    className="compact-filter-btn gap"
                    onClick={() => setCompactDialog(prev => ({
                      ...prev,
                      selected: new Set(prev.candidates.filter(c => c.category !== 'risky').map(c => c.id))
                    }))}
                  >
                    + Giảm trống ({counts.gap})
                  </button>
                )}
                {counts.risky > 0 && (
                  <button
                    className="compact-filter-btn all"
                    onClick={() => setCompactDialog(prev => ({
                      ...prev,
                      selected: new Set(prev.candidates.map(c => c.id))
                    }))}
                  >
                    Tất cả ({candidates.length})
                  </button>
                )}
                <button
                  className="compact-filter-btn none"
                  onClick={() => setCompactDialog(prev => ({ ...prev, selected: new Set() }))}
                >
                  Bỏ chọn
                </button>
              </div>

              <div className="dialog-body" style={{ maxHeight: '55vh', overflowY: 'auto', paddingTop: 0 }}>
                <div className="compact-swap-list">
                  {candidates.map(c => {
                    const isSelected = selected.has(c.id);
                    const catLabel = { safe: 'An toàn', gap: 'Giảm trống', risky: 'Rủi ro' }[c.category];
                    return (
                      <div
                        key={c.id}
                        className={`compact-swap-item ${c.category}${isSelected ? ' selected' : ''}`}
                        onClick={() => {
                          if (executing) return;
                          setCompactDialog(prev => {
                            const next = new Set(prev.selected);
                            if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                            return { ...prev, selected: next };
                          });
                        }}
                      >
                        <div className="compact-swap-check">
                          <input type="checkbox" readOnly checked={isSelected} style={{ pointerEvents: 'none' }} />
                        </div>
                        <div className="compact-swap-body">
                          <div className="compact-swap-header">
                            <span className="compact-lop-badge">{c.lop_name}</span>
                            <span className={`compact-cat-badge ${c.category}`}>{catLabel}</span>
                            {(c.sessAEliminated || c.sessBEliminated) && (
                              <span className="compact-elim-badge">
                                🔓 Giải phóng {c.sessAEliminated && c.sessBEliminated ? '2 buổi' : '1 buổi'}
                              </span>
                            )}
                          </div>
                          <div className="compact-swap-rows">
                            {/* GV A */}
                            <div className="compact-gv-row">
                              <span className="compact-gv-name">{c.tkbA.ten_gv}</span>
                              <span className="compact-mon">{c.tkbA.ten_mon}</span>
                              <span className="compact-slot from">
                                T.{thuLabel(c.tkbA.thu)} {buoiLabel(c.tkbA.buoi)} Tiết {c.tkbA.tiet}
                              </span>
                              <span className="compact-arrow">→</span>
                              <span className="compact-slot to">
                                T.{thuLabel(c.tkbB.thu)} {buoiLabel(c.tkbB.buoi)} Tiết {c.tkbB.tiet}
                              </span>
                              <span className={`compact-sess-badge${c.deltaSessA < 0 ? ' good' : c.deltaSessA > 0 ? ' bad' : ''}`}>
                                {c.sessA_before}→{c.sessA_after} buổi
                                {c.deltaSessA < 0 ? ` (−${-c.deltaSessA})` : c.deltaSessA > 0 ? ` (+${c.deltaSessA})` : ' (=)'}
                              </span>
                            </div>
                            {/* GV B */}
                            <div className="compact-gv-row">
                              <span className="compact-gv-name">{c.tkbB.ten_gv}</span>
                              <span className="compact-mon">{c.tkbB.ten_mon}</span>
                              <span className="compact-slot from">
                                T.{thuLabel(c.tkbB.thu)} {buoiLabel(c.tkbB.buoi)} Tiết {c.tkbB.tiet}
                              </span>
                              <span className="compact-arrow">→</span>
                              <span className="compact-slot to">
                                T.{thuLabel(c.tkbA.thu)} {buoiLabel(c.tkbA.buoi)} Tiết {c.tkbA.tiet}
                              </span>
                              <span className={`compact-sess-badge${c.deltaSessB < 0 ? ' good' : c.deltaSessB > 0 ? ' bad' : ''}`}>
                                {c.sessB_before}→{c.sessB_after} buổi
                                {c.deltaSessB < 0 ? ` (−${-c.deltaSessB})` : c.deltaSessB > 0 ? ` (+${c.deltaSessB})` : ' (=)'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="dialog-footer">
                <button className="btn btn-secondary" onClick={() => setCompactDialog(prev => ({ ...prev, open: false }))} disabled={executing}>
                  Hủy
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleExecuteCompactSwaps}
                  disabled={executing || selected.size === 0}
                >
                  {executing ? 'Đang thực hiện...' : `Thực hiện ${selected.size} đổi chỗ`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <style>{`
        .tkb-page {
          height: 100%;
        }
        
        .tkb-container {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
          height: calc(100vh - 112px);
        }
        
        .tkb-main-panel {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        
        .panel-header {
          border-bottom: 1px solid #e5e7eb;
          background: #fff;
          border-radius: 8px 8px 0 0;
        }
        
        .panel-header h3 {
          margin: 0;
          font-size: 16px;
        }

        .panel-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .panel-tabs {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        

        
        .header-actions {
          display: flex;
          align-items: center;
          gap: 15px;
          flex-wrap: wrap;
        }
        
        .action-buttons {
          display: flex;
          gap: 8px;
        }
        
        .action-buttons .btn {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12px;
          padding: 6px 12px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
        }
        
        .action-buttons .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .action-buttons .btn-success {
          background: #10b981;
          color: white;
        }
        
        .action-buttons .btn-success:hover:not(:disabled) {
          background: #059669;
        }
        
        .action-buttons .btn-warning {
          background: #f59e0b;
          color: white;
        }
        
        .action-buttons .btn-warning:hover:not(:disabled) {
          background: #d97706;
        }
        
        .action-buttons .btn-danger {
          background: #ef4444;
          color: white;
        }
        
        .action-buttons .btn-danger:hover:not(:disabled) {
          background: #dc2626;
        }
        
        .edit-lock-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s;
          border: 2px solid transparent;
        }
        
        .edit-lock-toggle input {
          display: none;
        }
        
        .edit-lock-toggle.locked {
          background: #fee2e2;
          color: #dc2626;
          border-color: #fecaca;
        }
        
        .edit-lock-toggle.locked:hover {
          background: #fecaca;
        }
        
        .edit-lock-toggle.unlocked {
          background: #dcfce7;
          color: #16a34a;
          border-color: #bbf7d0;
        }
        
        .edit-lock-toggle.unlocked:hover {
          background: #bbf7d0;
        }

        .swap-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s;
          border: 2px solid #ddd;
          background: #fff;
        }

        .swap-toggle input {
          display: none;
        }

        .swap-toggle.active {
          border-color: #60a5fa;
          background: #e0f2fe;
          color: #1d4ed8;
        }

        .rei-o-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border: 1px solid #d97706;
          background: white;
          color: #92400e;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.15s;
        }

        .rei-o-btn:hover:not(:disabled) {
          background: #fef3c7;
        }

        .rei-o-btn.rei-o-ready {
          border-color: #d97706;
          background: #fef3c7;
          color: #78350f;
          font-weight: 600;
        }

        .rei-o-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .swap-toggle.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .block-mode-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 13px;
          background: #f5f5f5;
          border: 2px solid transparent;
          transition: all 0.2s;
        }
        
        .block-mode-toggle:hover {
          background: #e0e0e0;
        }
        
        .block-mode-toggle.active {
          background: #424242;
          color: white;
          border-color: #212121;
        }
        
        .block-mode-toggle input {
          display: none;
        }

        .result-panel {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding-top: 8px;
        }

        .result-toolbar {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          padding: 0 20px;
        }

        .result-gv-select {
          width: 260px;
          max-width: 100%;
        }
        
        .tkb-table-wrapper {
          flex: 1;
          overflow: auto;
          padding: 10px;
          user-select: none;
        }
        
        .tkb-table {
          border-collapse: collapse;
          font-size: 12px;
          width: max-content;
          min-width: 100%;
        }
        
        .tkb-table th,
        .tkb-table td {
          border: 1px solid #ddd;
          padding: 4px;
          text-align: center;
          min-width: 60px;
          height: 32px;
        }
        
        .tkb-table th {
          background: #f5f5f5;
          font-weight: 600;
          position: sticky;
          top: 0;
          z-index: 3;
        }
        
        .header-cell {
          background: #37474f !important;
          color: #fff !important;
          font-weight: 700 !important;
          font-size: 12px;
          letter-spacing: 0.3px;
        }
        
        .sticky-col {
          position: sticky;
          left: 0;
          background: #f0f0f0;
          z-index: 4;
          min-width: 35px;
          font-weight: bold;
        }

        .sticky-col-2 {
          position: sticky;
          left: 35px;
          background: #f5f5f5;
          z-index: 4;
          min-width: 30px;
        }

        .sticky-col-3 {
          position: sticky;
          left: 65px;
          background: #fafafa;
          z-index: 4;
          min-width: 30px;
        }

        /* Header sticky cols cần giữ màu tối từ header-cell */
        th.sticky-col.header-cell,
        th.sticky-col-2.header-cell,
        th.sticky-col-3.header-cell {
          background: #263238 !important;
          color: #fff !important;
        }
        
        .thu-cell {
          background: #e3f2fd !important;
          font-weight: bold;
          font-size: 14px;
        }
        
        .buoi-cell {
          background: #fff3e0 !important;
          font-weight: 600;
        }
        
        .buoi-chieu {
          background: #e3f2fd !important;
          border-top: 3px solid #1976d2 !important;
          box-shadow: inset 0 3px 0 0 rgba(25, 118, 210, 0.3);
        }
        
        .buoi-chieu-row {
          border-top: 4px solid #1976d2 !important;
          box-shadow: 0 2px 4px rgba(25, 118, 210, 0.2);
        }
        
        .thu-separator {
          border-top: 3px solid #2e7d32 !important;
        }
        
        .tiet-cell {
          background: #f5f5f5 !important;
        }
        
        /* Dòng tạm thời */
        .temp-row {
          background: #fafafa !important;
        }
        
        .temp-separator {
          border-top: 4px solid #ff9800 !important;
          box-shadow: 0 2px 4px rgba(255, 152, 0, 0.3);
        }
        
        .temp-thu-cell {
          background: #fff3e0 !important;
          font-weight: bold;
          font-size: 14px;
          color: #e65100;
        }
        
        .temp-buoi-cell {
          background: #fff8e1 !important;
          font-weight: 600;
          color: #ef6c00;
        }
        
        .temp-tiet-cell {
          background: #ffecb3 !important;
          color: #e65100;
          font-weight: 500;
        }

        .conflict-slot {
          background: var(--conflict-bg) !important;
          color: var(--conflict-text) !important;
          cursor: pointer;
        }
        
        .temp-cell {
          background: #fff9f5 !important;
          border: 1px dashed #ff9800 !important;
          cursor: default !important;
        }
        
        .temp-cell:hover {
          background: #fff3e0 !important;
        }
        
        .temp-content {
          opacity: 0.8;
        }
        
        .temp-content .mon-name {
          color: #e65100 !important;
          font-style: italic;
        }
        
        .unassigned-unit {
          background: #ffeaea !important;
          border: 2px dashed #f44336 !important;
        }
        
        .unassigned-unit .mon-name {
          color: #d32f2f !important;
          font-weight: 600 !important;
        }
        
        .unassigned-marker {
          display: inline-block;
          background: #f44336;
          color: white;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          line-height: 16px;
          text-align: center;
          font-size: 12px;
          font-weight: bold;
          margin-right: 4px;
        }
        
        .lop-header {
          background: #455a64 !important;
          color: #fff !important;
          font-weight: 700 !important;
          font-size: 13px;
          letter-spacing: 0.2px;
          min-width: 70px;
        }
        
        .tkb-cell {
          cursor: pointer;
          transition: all 0.15s;
          vertical-align: middle;
        }
        
        .tkb-cell:not(.has-gv):hover {
          background: #e3f2fd;
        }
        
        /* Nền mặc định chỉ dùng khi không tô màu theo GV */
        .tkb-cell.has-tkb:not(.has-gv) {
          background: #fff9c4;
        }
        
        .tkb-cell.has-tkb:not(.has-gv):hover {
          background: #fff59d;
        }
        
        /* Ô đã tô màu theo GV: giữ nguyên nền, hover nhẹ */
        .tkb-cell.has-gv:hover {
          filter: brightness(0.95);
        }

        /* Khi click chọn 1 GV: các ô của GV đó sẽ "sáng lên" */
        .tkb-cell.gv-highlight {
          filter: brightness(1.25) saturate(1.15);
          box-shadow:
            inset 0 0 0 4px rgba(0, 0, 0, 0.55),
            0 0 0 2px rgba(255, 255, 255, 0.65),
            0 6px 16px rgba(0, 0, 0, 0.25);
        }

        /* Khi đang highlight 1 GV: làm mờ các ô còn lại để dễ nhìn */
        .tkb-table.has-highlight .tkb-cell.has-gv:not(.gv-highlight) {
          opacity: 0.35;
          filter: grayscale(0.35);
        }

        .tkb-table.has-highlight .tkb-cell.has-gv:not(.gv-highlight):hover {
          opacity: 0.55;
        }
        
        .tkb-cell.can-add {
          background: #e8f5e9;
        }
        
        .tkb-cell.can-add:hover {
          background: #c8e6c9;
        }
        
        .tkb-cell.will-study {
          background: #e8f5e9 !important;
          border: 2px solid #4caf50 !important;
        }
        
        .tkb-cell.will-study:hover {
          background: #c8e6c9 !important;
        }
        
        .tkb-cell.block-mode {
          background: #e8f5e9;
        }
        
        .tkb-cell.block-mode:hover {
          background: #c8e6c9;
        }

        .tkb-cell.spread-eligible {
          box-shadow: inset 0 0 0 2px rgba(33, 150, 243, 0.9);
        }

        .tkb-cell.spread-eligible:hover {
          box-shadow: inset 0 0 0 2px rgba(25, 118, 210, 1);
        }

        .tkb-cell.spread-disabled {
          opacity: 0.7;
        }

        .tkb-cell.swap-source {
          box-shadow: inset 0 0 0 3px rgba(234, 88, 12, 0.9);
        }

        .tkb-cell.fixed-strong {
          box-shadow: inset 0 0 0 3px rgba(156, 39, 176, 0.9);
          filter: saturate(1.2);
        }
        
        .marked-study {
          color: #4caf50;
          font-weight: bold;
          font-size: 16px;
        }
        
        .tkb-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
        }
        
        .mon-name {
          font-weight: 600;
          font-size: 11px;
          color: inherit;
        }
        
        /* Dialog chọn buổi xếp TKB */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .schedule-session-dialog {
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          width: 400px;
          max-width: 90vw;
          overflow: hidden;
        }

        .overload-move-dialog {
          width: 620px;
        }

        .compact-dialog {
          width: 720px;
        }

        .compact-summary-bar {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e5e7eb;
          font-size: 13px;
          flex-wrap: wrap;
        }

        .compact-summary-item {
          color: #374151;
        }

        .compact-summary-item.good {
          color: #15803d;
          font-weight: 700;
          background: #dcfce7;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .compact-summary-item.bad {
          color: #b91c1c;
          font-weight: 700;
          background: #fee2e2;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .compact-summary-net {
          font-weight: 700;
          margin-left: auto;
          font-size: 14px;
        }

        .compact-filter-bar {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 20px;
          border-bottom: 1px solid #e5e7eb;
          flex-wrap: wrap;
        }

        .compact-filter-btn {
          border: none;
          border-radius: 6px;
          padding: 3px 10px;
          font-size: 12px;
          cursor: pointer;
          font-weight: 600;
          transition: opacity 0.15s;
        }

        .compact-filter-btn:hover { opacity: 0.8; }

        .compact-filter-btn.safe  { background: #dcfce7; color: #15803d; }
        .compact-filter-btn.gap   { background: #dbeafe; color: #1d4ed8; }
        .compact-filter-btn.all   { background: #fef3c7; color: #92400e; }
        .compact-filter-btn.none  { background: #f3f4f6; color: #6b7280; }

        .compact-swap-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 12px 0 4px;
        }

        .compact-swap-item {
          border: 1.5px solid #e5e7eb;
          border-radius: 8px;
          padding: 10px 12px;
          cursor: pointer;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          transition: border-color 0.15s, background 0.15s;
          background: #fafafa;
        }

        .compact-swap-item.safe   { border-left: 4px solid #22c55e; }
        .compact-swap-item.gap    { border-left: 4px solid #3b82f6; }
        .compact-swap-item.risky  { border-left: 4px solid #f59e0b; }

        .compact-swap-item:hover        { background: #f0f9ff; }
        .compact-swap-item.selected     { background: #eff6ff; border-color: #3b82f6; }
        .compact-swap-item.safe.selected   { border-color: #22c55e; background: #f0fdf4; }
        .compact-swap-item.risky.selected  { border-color: #f59e0b; background: #fffbeb; }

        .compact-swap-check {
          padding-top: 2px;
          flex-shrink: 0;
        }

        .compact-swap-body {
          flex: 1;
          min-width: 0;
        }

        .compact-swap-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
          flex-wrap: wrap;
        }

        .compact-lop-badge {
          font-weight: 700;
          font-size: 13px;
          background: #dbeafe;
          color: #1d4ed8;
          border-radius: 5px;
          padding: 2px 8px;
        }

        .compact-cat-badge {
          font-size: 11px;
          font-weight: 600;
          border-radius: 4px;
          padding: 1px 7px;
        }

        .compact-cat-badge.safe  { background: #dcfce7; color: #15803d; }
        .compact-cat-badge.gap   { background: #dbeafe; color: #1d4ed8; }
        .compact-cat-badge.risky { background: #fef3c7; color: #92400e; }

        .compact-elim-badge {
          font-size: 11px;
          font-weight: 600;
          background: #f0fdf4;
          color: #15803d;
          border: 1px solid #86efac;
          border-radius: 4px;
          padding: 1px 7px;
        }

        .compact-swap-rows {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .compact-gv-row {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          font-size: 13px;
        }

        .compact-gv-name {
          font-weight: 600;
          color: #1f2937;
          min-width: 90px;
        }

        .compact-mon {
          color: #6b7280;
          font-style: italic;
          min-width: 60px;
        }

        .compact-slot {
          padding: 2px 7px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .compact-slot.from {
          background: #fee2e2;
          color: #b91c1c;
        }

        .compact-slot.to {
          background: #dcfce7;
          color: #15803d;
        }

        .compact-arrow {
          color: #9ca3af;
          font-size: 14px;
        }

        .compact-sess-badge {
          font-size: 12px;
          padding: 2px 7px;
          border-radius: 4px;
          background: #f3f4f6;
          color: #374151;
          margin-left: auto;
          white-space: nowrap;
        }

        .compact-sess-badge.good {
          background: #dcfce7;
          color: #15803d;
          font-weight: 600;
        }

        .compact-sess-badge.bad {
          background: #fee2e2;
          color: #b91c1c;
          font-weight: 600;
        }

        .overload-gv-info {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          padding: 10px 14px;
          background: #fff3cd;
          border: 1px solid #ffc107;
          border-radius: 8px;
        }

        .overload-gv-name {
          font-weight: 700;
          color: #333;
        }

        .overload-gv-badge {
          background: #dc3545;
          color: white;
          font-size: 12px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .overload-gv-note {
          color: #856404;
          font-size: 13px;
        }

        .overload-two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .overload-col-label {
          font-weight: 600;
          color: #555;
          font-size: 13px;
          margin-bottom: 6px;
        }

        .overload-unit-list,
        .overload-slot-list {
          border: 1px solid #ddd;
          border-radius: 6px;
          overflow-y: auto;
          max-height: 220px;
        }

        .overload-unit-item,
        .overload-slot-item {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 10px;
          cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
          transition: background 0.15s;
          font-size: 13px;
        }

        .overload-unit-item:last-child,
        .overload-slot-item:last-child {
          border-bottom: none;
        }

        .overload-unit-item:hover,
        .overload-slot-item:hover {
          background: #f0f4ff;
        }

        .overload-unit-item.selected,
        .overload-slot-item.selected {
          background: #e0e9ff;
          border-left: 3px solid #4a6cf7;
        }

        .unit-mon {
          font-weight: 600;
          color: #222;
          flex: 1;
        }

        .unit-lop {
          color: #555;
          font-size: 12px;
        }

        .unit-tiet {
          color: #888;
          font-size: 12px;
          white-space: nowrap;
        }

        .slot-thu {
          font-weight: 600;
          color: #333;
          width: 32px;
        }

        .slot-buoi {
          color: #555;
          font-size: 12px;
          width: 36px;
        }

        .slot-tiet {
          color: #666;
          font-size: 12px;
        }

        .overload-no-slots {
          padding: 24px 12px;
          color: #999;
          font-style: italic;
          font-size: 13px;
          text-align: center;
          border: 1px solid #ddd;
          border-radius: 6px;
        }
        
        .dialog-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        
        .dialog-header h3 {
          margin: 0;
          font-size: 18px;
        }
        
        .dialog-header .close-btn {
          background: none;
          border: none;
          color: white;
          font-size: 20px;
          cursor: pointer;
          padding: 4px;
          display: flex;
          opacity: 0.8;
          transition: opacity 0.2s;
        }
        
        .dialog-header .close-btn:hover {
          opacity: 1;
        }
        
        .dialog-body {
          padding: 20px;
        }
        
        .dialog-desc {
          margin: 0 0 16px 0;
          color: #666;
          font-size: 14px;
          line-height: 1.5;
        }
        
        .dialog-body .form-group {
          margin-bottom: 16px;
        }
        
        .dialog-body label {
          display: block;
          margin-bottom: 6px;
          font-weight: 600;
          color: #333;
        }
        
        .dialog-body .form-control {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s;
        }
        
        .dialog-body .form-control:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        
        .dialog-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 16px 20px;
          border-top: 1px solid #eee;
          background: #f8f9fa;
        }
        
        .dialog-footer .btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .dialog-footer .btn-secondary {
          background: #e0e0e0;
          border: none;
          color: #333;
        }
        
        .dialog-footer .btn-secondary:hover {
          background: #d0d0d0;
        }
        
        .dialog-footer .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          color: white;
        }
        
        .dialog-footer .btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }
        
        /* Nút mới */
        .btn-info {
          background: linear-gradient(135deg, #17a2b8 0%, #138496 100%) !important;
        }
        
        .btn-info:hover:not(:disabled) {
          background: linear-gradient(135deg, #138496 0%, #0f6674 100%) !important;
        }
        
        @media (max-width: 900px) {
          .tkb-container {
            grid-template-columns: 1fr;
            height: auto;
          }
          
          .tkb-main-panel {
            min-height: 400px;
          }
        }
      `}</style>

      {/* Panel tiến trình Tạo TKB */}
      {(isAutoScheduling || scheduleLog.length > 0) && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          width: 360, maxHeight: 480,
          background: '#fff', borderRadius: 10,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column',
          border: '1px solid #dee2e6', overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', background: '#1a73e8', color: '#fff',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14 }}>
              {isAutoScheduling && (
                <span style={{
                  width: 14, height: 14, borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff',
                  display: 'inline-block', animation: 'spin 0.8s linear infinite',
                }} />
              )}
              Tạo TKB tự động
            </div>
            {!isAutoScheduling && (
              <button
                onClick={() => { scheduleLogRef.current = []; setScheduleLog([]); }}
                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
                title="Đóng"
              >×</button>
            )}
          </div>
          {/* Log entries */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px', fontSize: 13, lineHeight: '1.7' }}>
            {scheduleLog.map(entry => (
              <div key={entry.id} style={{ color: LOG_COLORS[entry.type] || '#333' }}>
                {entry.text}
              </div>
            ))}
            {isAutoScheduling && scheduleLog.length === 0 && (
              <div style={{ color: '#666' }}>Đang khởi động...</div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default TKBPage;
